<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="tasks-page">
    <section class="tasks-hero">
      <div class="tasks-hero__glow" aria-hidden="true"></div>
      <div class="tasks-hero__top">
        <div class="tasks-hero__identity">
          <span class="tasks-hero__icon"><v-icon>mdi-clipboard-check-multiple-outline</v-icon></span>
          <div>
            <span class="tasks-eyebrow">Centrum operacyjne</span>
            <h1>Zadania</h1>
            <p>Jedno miejsce do przyjmowania zgłoszeń, delegowania pracy i pilnowania terminów.</p>
          </div>
        </div>
        <div class="tasks-hero__actions">
          <button class="task-button task-button--ghost" type="button" @click="showStats = true">
            <v-icon size="18">mdi-chart-box-outline</v-icon>
            Statystyki
          </button>
          <button v-if="currentTab !== 'archive'" class="task-button task-button--primary" type="button" @click="openCreateDialog">
            <v-icon size="18">mdi-plus</v-icon>
            Nowe zadanie
          </button>
        </div>
      </div>

      <div class="tasks-metrics">
        <article v-for="metric in taskMetrics" :key="metric.key" class="task-metric" :class="`task-metric--${metric.tone}`">
          <span class="task-metric__icon"><v-icon size="19">{{ metric.icon }}</v-icon></span>
          <span class="task-metric__copy">
            <small>{{ metric.label }}</small>
            <strong>{{ metric.value }}</strong>
          </span>
          <span class="task-metric__hint">{{ metric.hint }}</span>
        </article>
      </div>
    </section>

    <section class="tasks-workspace">
      <header class="tasks-toolbar">
        <nav class="task-tabs" aria-label="Widok zadań">
          <button
            v-for="tab in taskTabs"
            :key="tab.value"
            type="button"
            class="task-tab"
            :class="{ 'is-active': currentTab === tab.value }"
            @click="currentTab = tab.value"
          >
            <v-icon size="17">{{ tab.icon }}</v-icon>
            <span>{{ tab.label }}</span>
            <small v-if="tab.count !== null">{{ tab.count }}</small>
          </button>
        </nav>

        <div class="tasks-toolbar__controls">
          <v-text-field
            v-model="search"
            placeholder="Szukaj po tytule, treści lub osobie…"
            outlined dense hide-details
            prepend-inner-icon="mdi-magnify"
            class="task-search"
          />
          <v-select
            v-model="taskSort"
            :items="taskSortOptions"
            outlined dense hide-details
            prepend-inner-icon="mdi-sort-variant"
            class="task-sort"
          />
        </div>
        <div class="task-quick-filters" aria-label="Szybkie filtry zadań">
          <span>Widok:</span>
          <button
            v-for="filter in taskQuickFilters"
            :key="filter.value"
            type="button"
            :class="{ 'is-active': quickFilter === filter.value, 'is-urgent': filter.value === 'urgent' }"
            @click="quickFilter = filter.value"
          >
            <v-icon size="15">{{ filter.icon }}</v-icon>
            {{ filter.label }}
            <small v-if="filter.count !== null">{{ filter.count }}</small>
          </button>
        </div>
      </header>

      <div class="tasks-results-bar">
        <div>
          <span>{{ currentViewTitle }}</span>
          <strong>{{ totalItems }} {{ taskCountLabel(totalItems) }}</strong>
        </div>
        <span class="tasks-results-bar__filter">
          <v-icon size="14">mdi-sort-calendar-ascending</v-icon> sekcje porządkują bieżącą stronę według terminu
        </span>
      </div>

      <div v-if="loading" class="task-skeletons" aria-live="polite">
        <v-skeleton-loader v-for="n in 4" :key="n" type="list-item-avatar-three-line" class="task-skeleton" />
      </div>

      <div v-else-if="filteredTasks.length" class="task-groups">
        <section v-for="group in groupedTasks" :key="group.key" class="task-group" :class="`task-group--${group.tone}`">
          <header class="task-group__header">
            <span><v-icon size="18">{{ group.icon }}</v-icon></span>
            <div><h2>{{ group.label }}</h2><p>{{ group.hint }}</p></div>
          </header>
          <div class="task-list">
        <article
          v-for="item in group.items"
          :key="item.id"
          class="task-card"
          :class="[taskCardClasses(item), { 'is-selected': previewDialog && previewTask.id === item.id }]"
          role="button"
          tabindex="0"
          @click="openPreviewFromRow(item)"
          @keydown.enter="openPreviewFromRow(item)"
        >
          <span class="task-card__accent" aria-hidden="true"></span>

          <div class="task-card__content">
            <div class="task-card__badges">
              <span class="task-badge" :class="item.driverCreatedBy ? 'task-badge--driver' : 'task-badge--internal'">
                <v-icon size="13">{{ item.driverCreatedBy ? 'mdi-steering' : 'mdi-office-building-outline' }}</v-icon>
                {{ item.driverCreatedBy ? 'Zgłoszenie kierowcy' : taskTypeLabel(item.type) }}
              </span>
              <span v-if="item.priority === 'HIGH' || item.priority === 'CRITICAL'" class="task-badge" :class="`task-badge--priority-${String(item.priority).toLowerCase()}`">
                <v-icon size="13">{{ getPriorityIcon(item.priority) }}</v-icon>
                {{ getPriorityText(item.priority) }} priorytet
              </span>
              <span v-if="taskUnreadCount(item)" class="task-badge task-badge--unread">
                <i></i><v-icon size="13">mdi-message-badge-outline</v-icon>Nowa odpowiedź<span v-if="taskUnreadCount(item) > 1"> · {{ taskUnreadCount(item) }}</span>
              </span>
            </div>

            <div class="task-card__open">
              <h2><v-icon v-if="item.important" size="16" class="task-title-star">mdi-star</v-icon>{{ item.title }}</h2>
              <p v-if="item.description">{{ item.description }}</p>
              <p v-else class="is-empty">Brak dodatkowego opisu.</p>
            </div>

            <div class="task-card__focus">
              <div class="task-focus-item task-focus-item--owner" :class="{ 'is-unassigned': !item.assignedUser }">
                <span class="task-avatar task-avatar--assignee">{{ taskAssigneeInitials(item) }}</span>
                <span><small>Osoba odpowiedzialna</small><strong>{{ taskAssigneeLabel(item) }}</strong></span>
              </div>
              <div class="task-focus-item task-focus-item--deadline" :class="`is-${dueDateTone(item.dueDate)}`">
                <span><v-icon size="19">{{ item.dueDate ? dueDateMeta(item.dueDate).icon : 'mdi-calendar-plus-outline' }}</v-icon></span>
                <span><small>Termin działania</small><strong>{{ taskDeadlineHeadline(item) }}</strong><em>{{ taskDeadlineCaption(item) }}</em></span>
              </div>
            </div>

            <footer class="task-card__activity">
              <span :class="{ 'is-driver-reply': taskActivityMeta(item).actor === 'driver' }" :title="formatNoteDate(taskActivityMeta(item).date)">
                <v-icon size="15">{{ taskActivityIcon(item) }}</v-icon>{{ taskActivityLabel(item) }}
              </span>
              <span v-if="taskMessageCount(item)"><v-icon size="15">mdi-message-text-outline</v-icon>{{ taskMessageCount(item) }} {{ taskMessageCount(item) === 1 ? 'wiadomość' : 'wiadomości' }}</span>
              <span v-if="item.linkedRecordId" class="task-card__record"><v-icon size="15">mdi-link-variant</v-icon>{{ item.linkedRecordType || 'Rekord' }} #{{ shortRecordId(item.linkedRecordId) }}</span>
            </footer>
          </div>

          <aside class="task-card__side">
            <div class="task-card__side-top">
              <span class="task-status" :class="`task-status--${statusTone(item.status)}`">
                <span></span>{{ getStatusText(item.status) }}
              </span>
              <v-menu offset-y left content-class="task-status-menu task-actions-menu">
                <template v-slot:activator="{ on, attrs }">
                  <button type="button" class="task-more" aria-label="Więcej działań" v-bind="attrs" v-on="on" @click.stop>
                    <v-icon size="20">mdi-dots-horizontal</v-icon>
                  </button>
                </template>
                <v-list dense>
                  <v-list-item @click="openPreviewFromRow(item)">
                    <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Otwórz szczegóły</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="canAssignTasks && (item.status === 'NEW' || item.status === 'IN_PROGRESS')" @click="openAssignFromRow(item)">
                    <v-list-item-icon><v-icon small>mdi-account-arrow-right-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ item.assignedUser ? 'Zmień osobę' : 'Przypisz osobę' }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'IN_PROGRESS'" @click="openCompleteFromRow(item)">
                    <v-list-item-icon><v-icon small color="success">mdi-check-decagram-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Zakończ zadanie</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="toggleImportant(item)">
                    <v-list-item-icon><v-icon small :color="item.important ? 'amber' : undefined">{{ item.important ? 'mdi-star-off-outline' : 'mdi-star-outline' }}</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ item.important ? 'Usuń oznaczenie ważne' : 'Oznacz jako ważne' }}</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-subheader>Zmień status</v-subheader>
                  <v-list-item v-for="status in statusOptions" :key="status.value" :disabled="item.status === status.value" @click="changeStatus(item, status.value)">
                    <v-list-item-icon><v-icon small :color="status.color">{{ status.icon }}</v-icon></v-list-item-icon>
                    <v-list-item-title>{{ status.label }}</v-list-item-title>
                    <v-list-item-action v-if="item.status === status.value"><v-icon small>mdi-check</v-icon></v-list-item-action>
                  </v-list-item>
                  <v-divider />
                  <v-list-item class="task-actions-menu__danger" @click="deleteTask(item.id)">
                    <v-list-item-icon><v-icon small color="error">mdi-trash-can-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Usuń zadanie</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div v-if="item.status === 'COMPLETED' && item.completedAt" class="task-card__duration">
              <small>Czas realizacji</small>
              <strong>{{ formatElapsed(item.createdAt, item.completedAt) }}</strong>
            </div>

            <button
              v-if="item.status === 'NEW' && !item.assignedUser && canAssignTasks"
              type="button" class="task-primary-action task-primary-action--assign" @click.stop="openAssignFromRow(item)"
            ><v-icon size="17">mdi-account-arrow-right-outline</v-icon>Przypisz zadanie</button>
            <button
              v-else-if="item.status === 'IN_PROGRESS'"
              type="button" class="task-primary-action task-primary-action--reply" @click.stop="openReplyFromRow(item)"
            ><v-icon size="17">mdi-reply-outline</v-icon>Odpowiedz</button>
            <button
              v-else
              type="button" class="task-primary-action" @click.stop="openPreviewFromRow(item)"
            ><v-icon size="17">mdi-arrow-right</v-icon>Otwórz zadanie</button>
          </aside>
        </article>
          </div>
        </section>
      </div>

      <div v-else class="tasks-empty">
        <span><v-icon size="34">{{ search || quickFilter !== 'all' ? 'mdi-filter-off-outline' : 'mdi-clipboard-check-outline' }}</v-icon></span>
        <h2>{{ search || quickFilter !== 'all' ? 'Brak zadań spełniających filtry' : 'Wszystko pod kontrolą' }}</h2>
        <p>{{ search || quickFilter !== 'all' ? 'Wyczyść wyszukiwanie lub wybierz inny szybki filtr.' : 'W tym widoku nie ma teraz żadnych zadań.' }}</p>
        <button v-if="search || quickFilter !== 'all'" type="button" class="task-button task-button--ghost" @click="clearTaskFilters">Wyczyść filtry</button>
      </div>

      <footer v-if="totalItems > 0" class="tasks-pagination">
        <span>Wyświetlam {{ paginationFrom }}–{{ paginationTo }} z {{ totalItems }}</span>
        <v-pagination v-model="options.page" :length="totalPages" :total-visible="7" color="#5478FF" />
        <v-select v-model="options.itemsPerPage" :items="[10, 20, 50]" outlined dense hide-details class="tasks-page-size" suffix="/ str." />
      </footer>
    </section>

    <!-- Task Form Dialog -->
    <v-dialog content-class="dark-form-dialog task-modal task-modal--create" v-model="dialog" max-width="860px" @click:outside="closeDialog" @keydown.esc="closeDialog">
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--primary"><v-icon>mdi-clipboard-plus-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>{{ currentTaskId ? 'Aktualizacja zadania' : 'Nowe zadanie' }}</small>
            <strong>{{ currentTaskId ? $t('tasks.editTask') : 'Zaplanuj kolejne działanie' }}</strong>
            <span>Dodaj kontekst, ustal priorytet i wskaż osobę odpowiedzialną.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="task-modal__body">
          <div class="task-modal__section-title">
            <span>01</span><div><strong>Treść zadania</strong><small>Co dokładnie wymaga działania?</small></div>
          </div>
          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row class="task-form-grid">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.title"
                  :label="$t('tasks.taskTitle')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>


              <v-col cols="12">
                <v-textarea
                  v-model="formData.description"
                  :label="$t('tasks.taskDescription')"
                  rows="3"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.type"
                  :items="typeOptions"
                  :label="$t('tasks.taskType')"
                  :rules="[rules.required]"
                  @change="onTypeChange"
                  required
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.status"
                  :items="statusOptions"
                  :label="$t('common.status')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.priority"
                  :items="priorityOptions"
                  :label="$t('tasks.priority')"
                />
              </v-col>

              <v-col cols="12" md="6" v-if="formData.type === 'LINKED'">
                <v-select
                  v-model="formData.linkedRecordType"
                  :items="recordTypeOptions"
                  :label="$t('tasks.recordType')"
                  @change="loadLinkedRecords"
                />
              </v-col>

              <v-col cols="12" md="6" v-if="formData.type === 'LINKED' && formData.linkedRecordType">
                <v-select
                  v-model="formData.linkedRecordId"
                  :items="linkedRecords"
                  :loading="loadingLinkedRecords"
                  :label="$t('tasks.linkedRecord')"
                  item-text="name"
                  item-value="id"
                />
              </v-col>

              <v-col cols="12" md="6">
                <!-- Przy TWORZENIU mozna wskazac kilka osob — powstanie wtedy
                     osobne zadanie dla kazdej. Przy EDYCJI zostaje jedna osoba,
                     bo edytujemy konkretne, istniejace zadanie. -->
                <v-select
                  v-if="!currentTaskId"
                  v-model="formData.assignedUserIds"
                  :items="creationUserOptions"
                  :loading="loadingUsers"
                  :label="$t('tasks.assignTo')"
                  item-text="fullName"
                  item-value="id"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  :hint="formData.assignedUserIds && formData.assignedUserIds.length > 1
                    ? `Powstanie ${formData.assignedUserIds.length} osobnych zadan — kazda osoba zamyka swoje`
                    : 'Mozesz wskazac kilka osob naraz'"
                  persistent-hint
                />
                <v-select
                  v-else
                  v-model="formData.assignedUserId"
                  :items="creationUserOptions"
                  :loading="loadingUsers"
                  :label="$t('tasks.assignTo')"
                  item-text="fullName"
                  item-value="id"
                  clearable
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-menu
                  ref="dueDateMenu"
                  v-model="dueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                  content-class="dark-date-picker task-date-picker-menu"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.dueDate"
                      :label="$t('tasks.dueDate')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="formData.dueDate"
                    @input="dueDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
          </v-form>

          <!-- Screenshoty / załączniki przy tworzeniu (po zapisie lecą jako wpisy w wątku) -->
          <div class="task-upload-zone" @paste="onTaskPaste" tabindex="0">
            <div class="task-upload-zone__head">
              <span class="task-upload-zone__icon"><v-icon>mdi-cloud-upload-outline</v-icon></span>
              <span><strong>Materiały i screenshoty</strong><small>Dodaj plik albo wklej obraz ze schowka (Ctrl+V)</small></span>
              <v-btn small outlined color="primary" @click="$refs.taskFiles.click()">
                <v-icon small left>mdi-paperclip-plus</v-icon>Wybierz pliki
              </v-btn>
              <input ref="taskFiles" type="file" multiple accept="image/*,application/pdf" style="display:none" @change="onTaskFilesPicked" />
            </div>
            <div v-if="pendingAttachments.length" class="task-upload-list">
              <div v-for="(f, i) in pendingAttachments" :key="i" class="task-upload-item">
                <img v-if="f.preview" :src="f.preview" alt="Podgląd załącznika" />
                <div v-else class="task-upload-item__file"><v-icon>mdi-file-document-outline</v-icon></div>
                <span>{{ f.file.name }}</span>
                <v-btn icon x-small class="task-upload-item__remove" @click="removePendingAttachment(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
              </div>
            </div>
          </div>

          <!-- Notes Section — widoczna zawsze gdy task istnieje (żeby admin mógł odpisać nawet pustemu) -->
          <div v-if="currentTaskId" class="task-modal__thread-section">
            <v-divider></v-divider>
            <h4 class="mt-4 mb-2">{{ $t('tasks.notes') }}</h4>
            <div class="notes-container task-thread">
              <div
                v-for="note in taskNotes"
                :key="note.id"
                class="mb-3 pa-3"
                :class="noteRoleClass(note)"
                style="border-radius: 8px; border-left: 4px solid;"
                :style="noteRoleStyle(note)"
              >
                <div class="d-flex align-center mb-1">
                  <v-icon small :color="noteIconColor(note)" class="mr-2">
                    {{ noteIcon(note) }}
                  </v-icon>
                  <strong class="text-body-2">{{ noteAuthorLabel(note) }}</strong>
                  <v-spacer></v-spacer>
                  <small class="grey--text">{{ formatNoteDate(note.createdAt) }}</small>
                </div>
                <div v-if="note.content" class="note-body">{{ note.content }}</div>
                <div v-if="note.attachmentFileId" class="mt-2">
                  <div v-if="isImageMime(note.attachmentMime)">
                    <img
                      v-if="attachmentUrls[note.attachmentFileId]"
                      :src="attachmentUrls[note.attachmentFileId]"
                      :alt="note.attachmentName || 'Załącznik'"
                      style="max-width: 320px; max-height: 240px; border-radius: 8px; cursor: pointer;"
                      @click="openAttachmentInNewTab(note.attachmentFileId)"
                    />
                    <v-btn
                      v-else
                      x-small outlined
                      @click="loadAttachment(note.attachmentFileId, note.attachmentMime)"
                    >
                      <v-icon left x-small>mdi-image</v-icon>
                      {{ note.attachmentName || 'Pokaż obraz' }}
                    </v-btn>
                  </div>
                  <v-btn
                    v-else
                    x-small outlined
                    @click="downloadAttachment(note.attachmentFileId, note.attachmentName)"
                  >
                    <v-icon left x-small>mdi-paperclip</v-icon>
                    {{ note.attachmentName || 'Pobierz załącznik' }}
                  </v-btn>
                </div>
              </div>
              <div v-if="taskNotes.length === 0" class="text-center pa-4 grey--text">
                {{ $t('tasks.noMessages') }}
              </div>
            </div>

            <!-- Add Note Form -->
            <div class="mt-3">
              <v-textarea
                v-model="newNote"
                :label="$t('tasks.addNote')"
                rows="2"
                outlined
                dense
              ></v-textarea>
              <v-file-input
                v-model="newNoteAttachment"
                label="Załączniki / zdjęcia (opcjonalnie)"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                outlined dense show-size clearable multiple counter
                prepend-icon="mdi-paperclip"
                hide-details
                class="mb-2"
                :rules="[v => !v || !v.length || v.every(f => f.size <= 10 * 1024 * 1024) || 'Każdy plik max 10 MB']"
              ></v-file-input>
              <v-btn
                color="primary"
                small
                @click="addNote"
                :disabled="!newNote.trim() && !newNoteAttachment.length"
                :loading="addingNote"
              >
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('tasks.addNote') }}
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="task-modal__actions">
          <v-btn
            v-if="currentTaskId && canAssignTasks"
            color="warning"
            outlined
            @click="openAssignDialog"
          >
            <v-icon left small>mdi-account-arrow-right</v-icon>Przypisz
          </v-btn>
          <v-spacer />
          <v-btn text class="task-modal__cancel" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            class="task-modal__submit"
            :disabled="!valid"
            :loading="submitting"
            @click="submitForm"
          >
            {{ currentTaskId ? $t('common.save') : $t('tasks.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Przypisz zadanie -->
    <v-dialog content-class="dark-form-dialog task-modal task-modal--assign" v-model="assignDialog" max-width="620">
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--amber"><v-icon>mdi-account-arrow-right-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>Delegowanie pracy</small>
            <strong>Przypisz zadanie</strong>
            <span>Wskaż właściciela zadania i ustaw realny termin.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="assignDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="task-modal__body">
          <!-- Podgląd zgłoszenia: żeby wiedzieć KOMU przypisać zanim się przypisze -->
          <div class="task-context-card">
            <span class="task-context-card__eyebrow">Kontekst zadania</span>
            <strong v-if="assignTaskTitle">{{ assignTaskTitle }}</strong>
            <small v-if="assignTaskDriver"><v-icon size="14">mdi-steering</v-icon> Zgłosił: {{ assignTaskDriver }}</small>
          </div>
          <div class="task-thread task-thread--compact">
            <div v-if="assignTaskDescription" class="mb-2">
              <div class="caption grey--text">Treść zgłoszenia:</div>
              <div class="text-body-2" style="white-space:pre-wrap;">{{ assignTaskDescription }}</div>
            </div>
            <div v-if="loadingAssignPreview" class="text-center py-2">
              <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
            </div>
            <div v-for="note in chronologicalNotes(assignTaskNotes)" :key="note.id" class="mb-2 pa-2" :class="noteRoleClass(note)" :style="noteRoleStyle(note)" style="border-radius:8px; border-left:4px solid;">
              <div class="d-flex align-center mb-1">
                <v-icon x-small :color="noteIconColor(note)" class="mr-1">{{ noteIcon(note) }}</v-icon>
                <strong class="caption">{{ noteAuthorLabel(note) }}</strong>
                <v-spacer></v-spacer>
                <small class="grey--text">{{ formatNoteDate(note.createdAt) }}</small>
              </div>
              <div v-if="note.content" class="note-body" style="white-space:pre-wrap; font-size:14px;">{{ note.content }}</div>
            </div>
            <div v-if="!loadingAssignPreview && !assignTaskNotes.length && !assignTaskDescription" class="caption grey--text text-center py-2">
              Brak treści zgłoszenia
            </div>
          </div>

          <div class="task-modal__section-title task-modal__section-title--spaced">
            <span>02</span><div><strong>Odpowiedzialność</strong><small>Kto i do kiedy powinien się tym zająć?</small></div>
          </div>
          <v-autocomplete
            v-model="assignForm.assignedUserId"
            :items="adminSelectOptions"
            item-text="name"
            item-value="id"
            label="Osoba przypisana"
            outlined dense
            :loading="loadingAdmins"
            no-data-text="Brak osób — sprawdź uprawnienia"
            clearable
            prepend-inner-icon="mdi-account"
          />
          <v-select
            v-model="assignForm.priority"
            :items="priorityOptions"
            item-text="text"
            item-value="value"
            label="Priorytet"
            outlined dense
            prepend-inner-icon="mdi-flag"
            :hint="priorityHint"
            persistent-hint
            @change="onPriorityChange"
          />
          <v-text-field
            v-model="assignForm.dueDate"
            type="date"
            label="Termin wykonania (auto z priorytetu)"
            outlined dense
            :min="todayDate"
            prepend-inner-icon="mdi-calendar-clock"
            class="mt-3"
          />
          <div class="task-inline-info">
            <v-icon size="18">mdi-bell-check-outline</v-icon>
            <span v-if="assignForm.assignedUserId">Po zapisie status zmieni się na <strong>W trakcie</strong>, a wskazana osoba otrzyma powiadomienie.</span>
            <span v-else>Zapiszesz zadanie bez osoby odpowiedzialnej. Możesz przypisać je później.</span>
          </div>
        </v-card-text>
        <v-card-actions class="task-modal__actions">
          <v-spacer />
          <v-btn text class="task-modal__cancel" @click="assignDialog = false">Anuluj</v-btn>
          <v-btn color="warning" class="task-modal__submit task-modal__submit--amber" :loading="submittingAssign" @click="submitAssign">
            <v-icon small left>mdi-check</v-icon>{{ assignForm.assignedUserId ? 'Przypisz zadanie' : 'Zapisz bez przypisania' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Odpowiedz -->
    <v-dialog content-class="dark-form-dialog task-modal task-modal--conversation" v-model="replyDialog" max-width="720">
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--primary"><v-icon>mdi-reply-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>Rozmowa w zadaniu</small>
            <strong>Odpowiedz zgłaszającemu</strong>
            <span>Wiadomość i załączniki trafią do wspólnego wątku.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="replyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="task-modal__body">
          <div v-if="replyTaskTitle" class="task-context-card">
            <span class="task-context-card__eyebrow">Odpowiadasz w zadaniu</span>
            <strong>{{ replyTaskTitle }}</strong>
          </div>

          <!-- Podgląd zgłoszenia: opis + wiadomości (na co odpisujesz) -->
          <div class="task-thread task-thread--reply">
            <div v-if="replyTaskDescription" class="mb-2">
              <div class="caption grey--text">Treść zgłoszenia:</div>
              <div class="text-body-2" style="white-space:pre-wrap;">{{ replyTaskDescription }}</div>
            </div>
            <div v-if="loadingReplyNotes" class="text-center py-2">
              <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
            </div>
            <div v-for="note in chronologicalNotes(replyNotes)" :key="note.id" class="mb-2 pa-2" :class="noteRoleClass(note)" :style="noteRoleStyle(note)" style="border-radius:8px; border-left:4px solid;">
              <div class="d-flex align-center mb-1">
                <v-icon x-small :color="noteIconColor(note)" class="mr-1">{{ noteIcon(note) }}</v-icon>
                <strong class="caption">{{ noteAuthorLabel(note) }}</strong>
                <v-spacer></v-spacer>
                <small class="grey--text">{{ formatNoteDate(note.createdAt) }}</small>
              </div>
              <div v-if="note.content" class="note-body" style="white-space:pre-wrap; font-size:14px;">{{ note.content }}</div>
              <div v-if="note.attachmentFileId" class="mt-1">
                <img
                  v-if="isImageMime(note.attachmentMime) && attachmentUrls[note.attachmentFileId]"
                  :src="attachmentUrls[note.attachmentFileId]"
                  style="max-width:200px; max-height:160px; border-radius:6px; cursor:pointer;"
                  @click="openAttachmentInNewTab(note.attachmentFileId)"
                />
                <v-btn v-else x-small outlined
                  @click="isImageMime(note.attachmentMime) ? loadAttachment(note.attachmentFileId, note.attachmentMime) : downloadAttachment(note.attachmentFileId, note.attachmentName)">
                  <v-icon left x-small>mdi-paperclip</v-icon>{{ note.attachmentName || 'Załącznik' }}
                </v-btn>
              </div>
            </div>
            <div v-if="!loadingReplyNotes && !replyNotes.length && !replyTaskDescription" class="caption grey--text text-center py-2">
              Brak treści zgłoszenia
            </div>
          </div>

          <div class="task-composer">
            <div class="task-modal__section-title">
              <span><v-icon size="17">mdi-message-text-outline</v-icon></span><div><strong>Twoja odpowiedź</strong><small>Możesz dołączyć wiele plików, maks. 10 MB każdy.</small></div>
            </div>
            <v-textarea
              v-model="replyText"
              label="Treść odpowiedzi"
              outlined dense rows="4"
              auto-grow
              placeholder="Napisz konkretną informację dla zgłaszającego…"
            />
            <v-file-input
              v-model="replyAttachment"
              label="Dodaj zdjęcia lub dokumenty"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              outlined dense show-size clearable multiple counter
              prepend-inner-icon="mdi-paperclip"
              prepend-icon=""
              :rules="[v => !v || !v.length || v.every(f => f.size <= 10 * 1024 * 1024) || 'Każdy plik max 10 MB']"
            ></v-file-input>
          </div>
        </v-card-text>
        <v-card-actions class="task-modal__actions">
          <v-spacer />
          <v-btn text class="task-modal__cancel" @click="replyDialog = false">Anuluj</v-btn>
          <v-btn color="primary" class="task-modal__submit" :disabled="!replyText.trim() && !replyAttachment.length" :loading="submittingReply" @click="submitReply">
            <v-icon small left>mdi-send</v-icon>Wyślij
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Zakończ zadanie -->
    <v-dialog content-class="dark-form-dialog task-modal task-modal--complete" v-model="completeDialog" max-width="560">
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--green"><v-icon>mdi-check-decagram-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>Finalizacja</small>
            <strong>Zakończ zadanie</strong>
            <span>Zostaw krótkie podsumowanie wykonanych działań.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="completeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="task-modal__body">
          <div v-if="completeTaskTitle" class="task-context-card task-context-card--success">
            <span class="task-context-card__eyebrow">Zamykasz zadanie</span>
            <strong>{{ completeTaskTitle }}</strong>
          </div>
          <v-textarea
            v-model="completeNote"
            label="Opis zakończenia (wymagany)"
            outlined dense rows="4"
            placeholder="Opisz krótko jak zostało wykonane to zadanie..."
            :rules="[v => !!v && v.trim().length > 0 || 'Opis jest wymagany']"
            counter="1000"
            maxlength="1000"
          />
          <div class="task-inline-info task-inline-info--success">
            <v-icon size="18">mdi-shield-check-outline</v-icon>
            <span>Podsumowanie zostanie zapisane w historii zadania i pomoże w późniejszym audycie.</span>
          </div>
        </v-card-text>
        <v-card-actions class="task-modal__actions">
          <v-spacer />
          <v-btn text class="task-modal__cancel" @click="completeDialog = false">Anuluj</v-btn>
          <v-btn color="success" class="task-modal__submit task-modal__submit--green" :disabled="!completeNote.trim()" :loading="submittingComplete" @click="submitComplete">
            <v-icon small left>mdi-check</v-icon>Zakończ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Boczny panel: szczegóły i rozmowa w zadaniu -->
    <v-navigation-drawer
      v-model="previewDialog"
      right temporary fixed
      :width="taskDetailWidth"
      class="task-detail-drawer"
    >
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--primary"><v-icon>mdi-clipboard-text-search-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>Szczegóły i historia</small>
            <strong>Centrum zadania</strong>
            <span>Pełny kontekst, odpowiedzialność i rozmowa w jednym miejscu.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="task-modal__body">
          <div class="task-preview-summary">
            <div class="task-preview-summary__title">
              <span>
                <span class="task-status" :class="`task-status--${statusTone(previewTask.status)}`"><i></i>{{ getStatusText(previewTask.status) }}</span>
                <span v-if="previewTask.priority" class="task-badge" :class="`task-badge--priority-${String(previewTask.priority).toLowerCase()}`">
                  <v-icon size="13">{{ getPriorityIcon(previewTask.priority) }}</v-icon>{{ getPriorityText(previewTask.priority) }}
                </span>
              </span>
              <h2>{{ previewTask.title }}</h2>
            </div>
            <div class="task-preview-summary__grid">
              <span><v-icon size="17">mdi-account-circle-outline</v-icon><small>Zgłosił</small><strong>{{ previewAuthorLabel }}</strong></span>
              <span><v-icon size="17">mdi-account-check-outline</v-icon><small>Odpowiada</small><strong>{{ taskAssigneeLabel(previewTask) }}</strong></span>
              <span><v-icon size="17">mdi-calendar-plus</v-icon><small>Utworzono</small><strong>{{ formatDateOnly(previewTask.createdAt) }}</strong></span>
              <span><v-icon size="17">mdi-calendar-clock-outline</v-icon><small>Termin</small><strong>{{ previewTask.dueDate ? dueDateMeta(previewTask.dueDate).dateLabel + dueDateMeta(previewTask.dueDate).relLabel : 'Nie ustawiono' }}</strong></span>
            </div>
          </div>
          <div v-if="previewTask.completedAt" class="task-completion-strip">
            <v-icon size="19">mdi-check-decagram</v-icon>
            <span><small>Zakończono {{ formatNoteDate(previewTask.completedAt) }}</small><strong v-if="previewDurationLabel">Realizacja zajęła {{ previewDurationLabel }}</strong></span>
          </div>

          <!-- Treść zgłoszenia + pełny wątek wiadomości (w tym opis zakończenia) -->
          <div ref="previewThread" class="task-thread task-thread--preview">
            <div v-if="previewTask.description" class="mb-2">
              <div class="caption grey--text">Treść zgłoszenia:</div>
              <div class="text-body-2" style="white-space:pre-wrap;">{{ previewTask.description }}</div>
            </div>
            <div v-if="loadingPreview" class="text-center py-2">
              <v-progress-circular indeterminate size="18" width="2" color="primary"></v-progress-circular>
            </div>
            <!-- Zadanie rozesłane do kilku osób: rozmowa jest wspólna. Mówimy
                 o tym wprost, żeby nikt nie napisał czegoś w przekonaniu, że
                 pisze tylko do przełożonego. -->
            <v-alert
              v-if="previewTask.groupId"
              dense outlined type="info" class="mb-3 caption"
            >
              Zadanie wspólne — tę rozmowę widzą wszystkie osoby, którym je przydzielono.
              Status każdy zamyka u siebie.
            </v-alert>

            <div v-for="note in chronologicalNotes(previewNotes)" :key="note.id" class="mb-2 pa-2" :class="noteRoleClass(note)" :style="noteRoleStyle(note)" style="border-radius:8px; border-left:4px solid;">
              <div class="d-flex align-center mb-1">
                <v-icon x-small :color="noteIconColor(note)" class="mr-1">{{ noteIcon(note) }}</v-icon>
                <strong class="caption">{{ noteAuthorLabel(note) }}</strong>
                <v-spacer></v-spacer>
                <small class="grey--text">{{ formatNoteDate(note.createdAt) }}</small>
              </div>
              <div v-if="note.content" class="note-body" style="white-space:pre-wrap; font-size:14px;">{{ note.content }}</div>
              <div v-if="note.attachmentFileId" class="mt-1">
                <img
                  v-if="isImageMime(note.attachmentMime) && attachmentUrls[note.attachmentFileId]"
                  :src="attachmentUrls[note.attachmentFileId]"
                  style="max-width:200px; max-height:160px; border-radius:6px; cursor:pointer;"
                  @click="openAttachmentInNewTab(note.attachmentFileId)"
                />
                <v-btn v-else x-small outlined
                  @click="isImageMime(note.attachmentMime) ? loadAttachment(note.attachmentFileId, note.attachmentMime) : downloadAttachment(note.attachmentFileId, note.attachmentName)">
                  <v-icon left x-small>mdi-paperclip</v-icon>{{ note.attachmentName || 'Załącznik' }}
                </v-btn>
              </div>
            </div>
            <div v-if="!loadingPreview && !previewNotes.length && !previewTask.description" class="caption grey--text text-center py-2">
              Brak treści zgłoszenia
            </div>
          </div>
        </v-card-text>
        <!-- Odpowiedź — działa też w zadaniach zakończonych (przepływ informacji) -->
        <div class="task-preview-composer">
          <span class="task-preview-composer__icon"><v-icon size="19">mdi-message-reply-text-outline</v-icon></span>
          <v-text-field
            v-model="previewReply"
            placeholder="Napisz odpowiedź…"
            outlined dense hide-details
            @keyup.enter="sendPreviewReply"
            :disabled="sendingPreviewReply"
          />
          <v-btn
            color="primary"
            class="task-modal__submit"
            :loading="sendingPreviewReply"
            :disabled="!previewReply || !previewReply.trim()"
            @click="sendPreviewReply"
          >
            <v-icon left small>mdi-send</v-icon>Wyślij
          </v-btn>
        </div>
      </v-card>
    </v-navigation-drawer>

    <!-- Statistics Dialog -->
    <v-dialog content-class="dark-form-dialog task-modal task-modal--stats" v-model="showStats" max-width="920px">
      <v-card class="task-modal__card">
        <v-card-title class="task-modal__header">
          <span class="task-modal__icon task-modal__icon--violet"><v-icon>mdi-chart-box-outline</v-icon></span>
          <span class="task-modal__heading">
            <small>Przegląd operacyjny</small>
            <strong>{{ $t('tasks.statisticsTitle') }}</strong>
            <span>Szybki obraz obciążenia zespołu i tempa realizacji.</span>
          </span>
          <v-btn icon class="task-modal__close" aria-label="Zamknij" @click="showStats = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="task-modal__body">
          <div class="task-stats-grid">
            <article v-for="(stat, index) in statistics" :key="stat.label" :class="`is-tone-${index}`">
              <span><v-icon size="18">{{ ['mdi-clipboard-text-outline', 'mdi-inbox-arrow-down-outline', 'mdi-progress-clock', 'mdi-check-circle-outline', 'mdi-timer-outline'][index] || 'mdi-chart-box-outline' }}</v-icon></span>
              <small>{{ stat.label }}</small>
              <strong>{{ stat.value }}</strong>
            </article>
          </div>

          <div v-if="isAdmin" class="task-stats-team">
              <div class="task-modal__section-title task-modal__section-title--spaced">
                <span><v-icon size="17">mdi-account-group-outline</v-icon></span><div><strong>{{ $t('tasks.tasksByUsers') }}</strong><small>Rozkład pracy i czas realizacji w zespole.</small></div>
              </div>
              <v-data-table
                :headers="userStatsHeaders"
                :items="userStatistics"
                class="task-stats-table"
                :items-per-page="10"
                :footer-props="{ itemsPerPageText: 'Wierszy na stronę' }"
              />
          </div>
        </v-card-text>
        <v-card-actions class="task-modal__actions">
          <v-spacer />
          <v-btn color="primary" class="task-modal__submit" @click="showStats = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'
import { isSuperAdmin as isSuperAdminFn, canAssignTasks as canAssignTasksFn } from '@/services/permissions'

export default {
  name: 'TasksPage',
  data() {
    return {
      loading: false,
      submitting: false,
      loadingUsers: false,
      loadingLinkedRecords: false,
      dialog: false,
      showStats: false,
      dueDateMenu: false,
      valid: false,
      search: '',
      currentTab: 'active',
      quickFilter: 'all',
      taskSort: 'createdAt:desc',
      taskSortOptions: [
        { text: 'Najnowsze', value: 'createdAt:desc' },
        { text: 'Najstarsze', value: 'createdAt:asc' },
        { text: 'Najbliższy termin', value: 'dueDate:asc' },
        { text: 'Najwyższy priorytet', value: 'priority:desc' }
      ],
      // Assign dialog
      assignDialog: false,
      submittingAssign: false,
      assignForm: { assignedUserId: null, dueDate: '', priority: 'MEDIUM' },
      // Podgląd zgłoszenia w oknie przypisywania (żeby wiedzieć komu przypisać).
      assignTaskTitle: '', assignTaskDescription: '', assignTaskDriver: '', assignTaskNotes: [], loadingAssignPreview: false,
      adminsList: [],
      loadingAdmins: false,
      // Nadane przez head admina prawo przypisywania zadań (poza head/super).
      myCanAssignTasks: false,
      // Reply dialog
      replyDialog: false,
      replyTaskId: null,
      replyTaskTitle: '',
      replyTaskDescription: '',
      replyNotes: [],
      loadingReplyNotes: false,
      replyText: '',
      replyAttachment: [],
      submittingReply: false,
      newNoteAttachment: [],
      attachmentUrls: {},
      // Preview dialog — podgląd zadania (m.in. zakończonych w archiwum)
      previewDialog: false,
      previewReply: '',
      sendingPreviewReply: false,
      previewTask: {},
      previewNotes: [],
      loadingPreview: false,
      // Complete dialog
      completeDialog: false,
      completeTaskId: null,
      completeTaskTitle: '',
      completeNote: '',
      submittingComplete: false,
      currentTaskId: null,
      taskNotes: [],
      newNote: '',
      pendingAttachments: [], // screenshoty dodane w formularzu przed zapisem zadania
      addingNote: false,
      tasks: [],
      users: [],
      linkedRecords: [],
      statistics: [],
      userStatistics: [],
      taskSummary: { total: 0, new: 0, inProgress: 0, completed: 0, avgTime: '—' },
      taskFilterCounts: { all: 0, urgent: 0, unassigned: 0, drivers: 0 },
      unreadTaskNotifications: {},
      totalItems: 0,
      options: { page: 1, itemsPerPage: 10, sortBy: ['createdAt'], sortDesc: [true] },
      headers: [
        { text: 'Tytuł', value: 'title' },
        { text: 'Typ', value: 'type', width: 120 },
        { text: 'Status', value: 'status', width: 120 },
        { text: 'Priorytet', value: 'priority', width: 100 },
        { text: 'Powiązany rekord', value: 'linkedRecord', width: 150 },
        { text: 'Przypisane do', value: 'assignedUser', width: 150 },
        { text: 'Utworzone przez', value: 'createdBy', width: 150 },
        { text: 'Utworzono', value: 'createdAt', width: 110 },
        { text: 'Czas realizacji', value: 'completionTime', sortable: false, width: 130 },
        { text: 'Termin', value: 'dueDate', width: 120 },
        { text: 'Akcje', value: 'actions', sortable: false, width: 120 }
      ],
      userStatsHeaders: [
        { text: 'Użytkownik', value: 'userName' },
        { text: 'Wszystkie', value: 'total' },
        { text: 'Nowe', value: 'new' },
        { text: 'W trakcie', value: 'inProgress' },
        { text: 'Zakończone', value: 'completed' },
        { text: 'Śr. czas / zadanie', value: 'avgTime' },
      ],
      formData: {
        title: '',
        description: '',
        type: 'INTERNAL',
        status: 'NEW',
        priority: 'MEDIUM',
        assignedUserId: null,
        assignedUserIds: [],
        dueDate: null,
        linkedRecordType: null,
        linkedRecordId: null
      },
      typeOptions: [
        { text: 'Wewnętrzne', value: 'INTERNAL' },
        { text: 'Powiązane z rekordem', value: 'LINKED' },
        { text: 'Problem z samochodem', value: 'CAR_ISSUE' },
        { text: 'Problem z aplikacją', value: 'APP_ISSUE' },
        { text: 'Problem z płatnościami', value: 'PAYMENT_ISSUE' },
        { text: 'Pytanie ogólne', value: 'GENERAL_QUESTION' },
        { text: 'Inne', value: 'OTHER' }
      ],
      statusOptions: [
        { text: 'Nowe',       label: 'Nowe',       value: 'NEW',         icon: 'mdi-circle-outline', color: 'blue' },
        { text: 'W trakcie',  label: 'W trakcie',  value: 'IN_PROGRESS', icon: 'mdi-progress-clock', color: 'orange' },
        { text: 'Zakończone', label: 'Zakończone', value: 'COMPLETED',   icon: 'mdi-check-circle',   color: 'green' },
        { text: 'Anulowane',  label: 'Anulowane',  value: 'CANCELLED',   icon: 'mdi-close-circle',   color: 'red' }
      ],
      priorityOptions: [
        { text: 'Niski', value: 'LOW' },
        { text: 'Średni', value: 'MEDIUM' },
        { text: 'Wysoki', value: 'HIGH' },
        { text: 'Krytyczny', value: 'CRITICAL' }
      ],
      recordTypeOptions: [
        { text: 'Bonus', value: 'bonuses' },
        { text: 'Faktura', value: 'invoices' },
        { text: 'Rozliczenie', value: 'settlements' },
        { text: 'Przychód', value: 'incomes' }
      ],
      rules: {
        required: value => !!value || 'To pole jest wymagane',
      }
    }
  },
  computed: {
    myAdminId() {
      // id zalogowanego admina z payloadu JWT ({ user: { id, ... } })
      try {
        const token = localStorage.getItem('token') || ''
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        return payload?.user?.id || null
      } catch (e) {
        return null
      }
    },
    isAdmin() {
      return isSuperAdminFn();
    },
    fromDriversCount() {
      return (this.tasks || []).filter(t => t.driverCreatedById).length;
    },
    filteredTasks() {
      return [...(this.tasks || [])];
    },
    taskQuickFilters() {
      const tasks = this.tasks || [];
      const counts = this.taskFilterCounts || {};
      const searching = !!String(this.search || '').trim();
      const visibleCount = (value, fallback) => searching
        ? (this.quickFilter === value ? this.totalItems : null)
        : fallback;
      return [
        { value: 'all', label: 'Wszystkie', icon: 'mdi-view-list-outline', count: visibleCount('all', counts.all ?? tasks.length) },
        { value: 'urgent', label: 'Pilne', icon: 'mdi-alert-circle-outline', count: visibleCount('urgent', counts.urgent ?? tasks.filter(task => this.isUrgentTask(task)).length) },
        { value: 'unassigned', label: 'Nieprzypisane', icon: 'mdi-account-alert-outline', count: visibleCount('unassigned', counts.unassigned ?? tasks.filter(task => !task.assignedUserId && !task.assignedUser).length) },
        { value: 'drivers', label: 'Od kierowców', icon: 'mdi-steering', count: visibleCount('drivers', counts.drivers ?? this.fromDriversCount) }
      ];
    },
    groupedTasks() {
      const items = this.filteredTasks || [];
      if (this.currentTab === 'archive') {
        return items.length ? [{ key: 'archive', label: 'Ostatnio zakończone', hint: 'Historia z bieżącej strony', icon: 'mdi-archive-check-outline', tone: 'done', items }] : [];
      }
      const definitions = [
        { key: 'overdue', label: 'Po terminie', hint: 'Wymagają reakcji w pierwszej kolejności', icon: 'mdi-calendar-alert', tone: 'danger' },
        { key: 'today', label: 'Na dziś', hint: 'Termin upływa dzisiaj', icon: 'mdi-calendar-today-outline', tone: 'today' },
        { key: 'soon', label: 'Najbliższe', hint: 'Termin w ciągu dwóch dni', icon: 'mdi-calendar-clock-outline', tone: 'soon' },
        { key: 'later', label: 'Zaplanowane', hint: 'Zadania z późniejszym terminem', icon: 'mdi-calendar-range-outline', tone: 'normal' },
        { key: 'undated', label: 'Bez terminu', hint: 'Wymagają zaplanowania', icon: 'mdi-calendar-remove-outline', tone: 'neutral' },
        { key: 'cancelled', label: 'Anulowane', hint: 'Pozostają w widoku aktywnym', icon: 'mdi-close-circle-outline', tone: 'cancelled' }
      ];
      return definitions
        .map(group => ({ ...group, items: items.filter(task => this.taskGroupKey(task) === group.key) }))
        .filter(group => group.items.length);
    },
    taskMetrics() {
      return [
        { key: 'new', label: 'Nowe', value: this.taskSummary.new, hint: 'czekają na start', icon: 'mdi-inbox-arrow-down-outline', tone: 'blue' },
        { key: 'progress', label: 'W trakcie', value: this.taskSummary.inProgress, hint: 'aktualnie realizowane', icon: 'mdi-progress-clock', tone: 'amber' },
        { key: 'completed', label: 'Zakończone', value: this.taskSummary.completed, hint: 'zamknięte łącznie', icon: 'mdi-check-circle-outline', tone: 'green' },
        { key: 'average', label: 'Średni czas', value: this.taskSummary.avgTime, hint: 'od zgłoszenia', icon: 'mdi-timer-sand-complete', tone: 'violet' }
      ];
    },
    taskTabs() {
      return [
        { value: 'active', label: 'Aktywne', icon: 'mdi-format-list-checks', count: Math.max(0, this.taskSummary.total - this.taskSummary.completed) },
        { value: 'my', label: 'Moje zadania', icon: 'mdi-account-check-outline', count: null },
        { value: 'archive', label: 'Archiwum', icon: 'mdi-archive-outline', count: this.taskSummary.completed }
      ];
    },
    currentViewTitle() {
      return ({ active: 'Aktywne zadania', my: 'Zadania przypisane do mnie', archive: 'Archiwum zadań' })[this.currentTab] || 'Zadania';
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / (this.options.itemsPerPage || 10)));
    },
    paginationFrom() {
      if (!this.totalItems) return 0;
      return ((this.options.page || 1) - 1) * (this.options.itemsPerPage || 10) + 1;
    },
    paginationTo() {
      return Math.min(this.totalItems, (this.options.page || 1) * (this.options.itemsPerPage || 10));
    },
    taskDetailWidth() {
      return this.$vuetify.breakpoint.smAndDown ? this.$vuetify.breakpoint.width : 680;
    },
    canAssignTasks() {
      // Manager też ma to uprawnienie historycznie; dodatkowo konkretny admin
      // może mieć nadane przez head admina prawo `canAssignTasks`.
      const role = (localStorage.getItem('role') || '').toLowerCase();
      return canAssignTasksFn() || role === 'manager' || this.myCanAssignTasks;
    },
    adminSelectOptions() {
      return (this.adminsList || []).map((admin) => {
        const workload = (this.userStatistics || []).find(stat => stat.userId === admin.id);
        const role = admin.role ? ` — ${admin.role}` : '';
        const active = workload ? ` · aktywne: ${workload.active ?? ((workload.new || 0) + (workload.inProgress || 0))}` : '';
        return {
          id: admin.id,
          name: `${admin.firstName || ''} ${admin.lastName || ''}`.trim() + role + active,
        };
      });
    },
    creationUserOptions() {
      return (this.users || []).map((user) => {
        const workload = (this.userStatistics || []).find(stat => stat.userId === user.id);
        const active = workload ? ` · aktywne: ${workload.active ?? ((workload.new || 0) + (workload.inProgress || 0))}` : '';
        return { ...user, fullName: `${user.fullName}${active}` };
      });
    },
    todayDate() {
      return new Date().toISOString().slice(0, 10);
    },
    previewAuthorLabel() {
      const t = this.previewTask || {};
      if (t.createdBy) return `${t.createdBy.firstName || ''} ${t.createdBy.lastName || ''}`.trim();
      if (t.driverCreatedBy) return `${t.driverCreatedBy.firstName || ''} ${t.driverCreatedBy.lastName || ''} (kierowca)`.trim();
      return 'System';
    },
    previewDurationLabel() {
      const t = this.previewTask || {};
      if (!t.completedAt || !t.createdAt) return '';
      const ms = new Date(t.completedAt) - new Date(t.createdAt);
      if (!(ms > 0)) return '';
      const totalMin = Math.floor(ms / 60000);
      const days = Math.floor(totalMin / 1440);
      const hours = Math.floor((totalMin % 1440) / 60);
      const mins = totalMin % 60;
      if (days >= 1) return `${days} dn. ${hours} godz.`;
      if (hours >= 1) return `${hours} godz. ${mins} min`;
      return `${Math.max(totalMin, 1)} min`;
    },
    priorityHint() {
      const p = this.assignForm?.priority;
      if (p === 'CRITICAL') return 'Krytyczny: termin = +24 h (do jutra)';
      if (p === 'HIGH') return 'Wysoki: termin = +24 h (do jutra)';
      if (p === 'MEDIUM') return 'Średni: termin = +48 h (pojutrze)';
      if (p === 'LOW') return 'Niski: termin = +72 h (za 3 dni)';
      return '';
    },
  },
  watch: {
    options: {
      handler() {
        this.loadTasks()
      },
      deep: true
    },
    taskSort(value) {
      const [sortBy, direction] = String(value || 'createdAt:desc').split(':')
      this.options = {
        ...this.options,
        page: 1,
        sortBy: [sortBy],
        sortDesc: [direction !== 'asc']
      }
    },
    search() {
      clearTimeout(this._taskSearchTimer)
      this._taskSearchTimer = setTimeout(() => {
        if (this.options.page && this.options.page !== 1) {
          this.options = { ...this.options, page: 1 }
        } else {
          this.loadTasks()
        }
      }, 320)
    },
    quickFilter() {
      this.previewDialog = false
      if (this.options.page && this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
      } else {
        this.loadTasks()
      }
    },
    currentTab() {
      this.previewDialog = false
      this.loadTaskFilterCounts()
      if (this.options.page && this.options.page !== 1) {
        this.options = { ...this.options, page: 1 }
      } else {
        this.loadTasks()
      }
    }
  },
  mounted() {
    this.loadTasks()
    this.loadTaskFilterCounts()
    this.loadUsers()
    this.loadStatistics()
    this.loadMyPermissions()
    this.loadUnreadTaskNotifications()
    // Deep-link z powiadomienia: ?taskId=... → otwórz podgląd tego zadania.
    if (this.$route.query.taskId) {
      this.openTaskById(this.$route.query.taskId)
    }
    this.$root.$on('task-notifications-updated', this.loadUnreadTaskNotifications)
    this._taskUnreadTimer = setInterval(() => {
      if (!document.hidden) this.loadUnreadTaskNotifications()
    }, 30000)
  },
  beforeDestroy() {
    clearTimeout(this._taskSearchTimer)
    clearInterval(this._taskUnreadTimer)
    this.$root.$off('task-notifications-updated', this.loadUnreadTaskNotifications)
    Object.values(this.attachmentUrls || {}).forEach(url => URL.revokeObjectURL(url))
  },
  methods: {
    openCreateDialog() {
      this.currentTaskId = null;
      this.formData = {
        title: '',
        description: '',
        type: 'INTERNAL',
        status: 'NEW',
        priority: 'MEDIUM',
        assignedUserId: null,
        assignedUserIds: [],
        dueDate: null,
        linkedRecordType: null,
        linkedRecordId: null
      };
      this.taskNotes = [];
      this.newNote = '';
      this.newNoteAttachment = [];
      this.pendingAttachments.forEach(a => a.preview && URL.revokeObjectURL(a.preview));
      this.pendingAttachments = [];
      this.dialog = true;
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    clearTaskFilters() {
      this.search = '';
      this.quickFilter = 'all';
    },
    taskCountLabel(count) {
      const value = Math.abs(Number(count) || 0);
      if (value === 1) return 'zadanie';
      if (value % 10 >= 2 && value % 10 <= 4 && !(value % 100 >= 12 && value % 100 <= 14)) return 'zadania';
      return 'zadań';
    },
    taskTypeLabel(type) {
      return ({
        INTERNAL: 'Zadanie wewnętrzne',
        LINKED: 'Powiązane z rekordem',
        CAR_ISSUE: 'Problem z autem',
        APP_ISSUE: 'Problem z aplikacją',
        PAYMENT_ISSUE: 'Problem z płatnością',
        GENERAL_QUESTION: 'Pytanie ogólne',
        OTHER: 'Inne zgłoszenie'
      })[type] || 'Zadanie';
    },
    getPriorityIcon(priority) {
      return ({ LOW: 'mdi-flag-outline', MEDIUM: 'mdi-flag-variant-outline', HIGH: 'mdi-alert-outline', CRITICAL: 'mdi-alert-octagon-outline' })[priority] || 'mdi-flag-variant-outline';
    },
    shortRecordId(id) {
      const value = String(id || '');
      return value.length > 8 ? value.slice(0, 8) : value;
    },
    initials(firstName, lastName, fallback = '—') {
      const chars = [firstName, lastName].filter(Boolean).map(value => String(value).trim().charAt(0)).join('').toUpperCase();
      return chars || fallback;
    },
    taskCreatorInitials(task) {
      const person = task.driverCreatedBy || task.createdBy;
      return person ? this.initials(person.firstName, person.lastName, task.driverCreatedBy ? 'K' : 'B') : 'B';
    },
    taskCreatorLabel(task) {
      const person = task.driverCreatedBy || task.createdBy;
      const fullName = person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '';
      if (fullName) return task.driverCreatedBy ? `${fullName} · kierowca` : fullName;
      return task.driverCreatedById ? 'Kierowca' : 'BTP';
    },
    taskAssigneeInitials(task) {
      const person = task.assignedUser;
      return person ? this.initials(person.firstName, person.lastName) : '—';
    },
    taskAssigneeLabel(task) {
      const person = task.assignedUser;
      const fullName = person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '';
      return fullName || 'Nieprzypisane';
    },
    statusTone(status) {
      return ({ NEW: 'new', IN_PROGRESS: 'progress', COMPLETED: 'done', CANCELLED: 'cancelled' })[status] || 'neutral';
    },
    taskDueDayDiff(dateString) {
      if (!dateString) return null;
      const due = new Date(dateString);
      if (Number.isNaN(due.getTime())) return null;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const target = new Date(due.getFullYear(), due.getMonth(), due.getDate());
      return Math.round((target - today) / 86400000);
    },
    dueDateTone(dateString) {
      const diff = this.taskDueDayDiff(dateString);
      if (diff === null) return 'neutral';
      if (diff < 0) return 'overdue';
      if (diff === 0) return 'today';
      if (diff <= 2) return 'soon';
      return 'normal';
    },
    isUrgentTask(task) {
      if (!task || task.status === 'COMPLETED' || task.status === 'CANCELLED') return false;
      const diff = this.taskDueDayDiff(task.dueDate);
      return task.priority === 'CRITICAL' || (diff !== null && diff <= 0);
    },
    taskGroupKey(task) {
      if (task.status === 'CANCELLED') return 'cancelled';
      const diff = this.taskDueDayDiff(task.dueDate);
      if (diff === null) return 'undated';
      if (diff < 0) return 'overdue';
      if (diff === 0) return 'today';
      if (diff <= 2) return 'soon';
      return 'later';
    },
    taskDeadlineHeadline(task) {
      if (!task?.dueDate) return 'Bez terminu';
      const diff = this.taskDueDayDiff(task.dueDate);
      if (diff === null) return 'Bez terminu';
      if (diff < -1) return `${Math.abs(diff)} dni po terminie`;
      if (diff === -1) return '1 dzień po terminie';
      if (diff === 0) return 'Termin dzisiaj';
      if (diff === 1) return 'Termin jutro';
      if (diff <= 7) return `Termin za ${diff} dni`;
      return this.formatDateOnly(task.dueDate);
    },
    taskDeadlineCaption(task) {
      return task?.dueDate ? this.formatDateOnly(task.dueDate) : 'Ustaw termin realizacji';
    },
    taskMessageCount(task) {
      return (task?.notes || []).filter(note => !note.internal).length;
    },
    taskUnreadCount(task) {
      return Number(this.unreadTaskNotifications?.[task?.id] || 0);
    },
    taskActivityMeta(task) {
      const updatedAt = task?.updatedAt || task?.createdAt;
      const latestNote = (task?.notes || []).reduce((latest, note) => {
        if (!latest) return note;
        return new Date(note.createdAt) > new Date(latest.createdAt) ? note : latest;
      }, null);
      const noteIsLatest = latestNote && (!updatedAt || new Date(latestNote.createdAt) >= new Date(updatedAt));
      const date = noteIsLatest ? latestNote.createdAt : updatedAt;
      let actor = 'task';
      if (noteIsLatest && latestNote.driverAuthorId) actor = 'driver';
      else if (noteIsLatest && latestNote.authorId) actor = 'admin';
      else if (noteIsLatest) actor = 'system';
      return { date, actor };
    },
    formatRelativeActivity(dateString) {
      if (!dateString) return 'brak aktywności';
      const diffMs = Date.now() - new Date(dateString).getTime();
      if (!Number.isFinite(diffMs)) return this.formatDateOnly(dateString);
      const minutes = Math.max(0, Math.floor(diffMs / 60000));
      if (minutes < 1) return 'przed chwilą';
      if (minutes < 60) return `${minutes} min temu`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} godz. temu`;
      const days = Math.floor(hours / 24);
      if (days === 1) return 'wczoraj';
      if (days < 14) return `${days} dni temu`;
      return this.formatDateOnly(dateString);
    },
    taskActivityLabel(task) {
      const activity = this.taskActivityMeta(task);
      const relative = this.formatRelativeActivity(activity.date);
      if (activity.actor === 'driver') return `Kierowca odpisał ${relative}`;
      if (activity.actor === 'admin') return `Zespół odpowiedział ${relative}`;
      if (activity.actor === 'system') return `Aktualizacja systemowa ${relative}`;
      return `Zaktualizowano ${relative}`;
    },
    taskActivityIcon(task) {
      return ({ driver: 'mdi-message-reply-text-outline', admin: 'mdi-message-check-outline', system: 'mdi-robot-outline', task: 'mdi-clock-outline' })[this.taskActivityMeta(task).actor];
    },
    taskCardClasses(task) {
      return {
        'is-important': !!task.important,
        'is-overdue': task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && this.dueDateTone(task.dueDate) === 'overdue',
        [`is-status-${this.statusTone(task.status)}`]: true
      };
    },
    async loadTaskFilterCounts() {
      const requestId = (this._taskCountsRequestId || 0) + 1;
      this._taskCountsRequestId = requestId;
      try {
        const queryParams = new URLSearchParams();
        if (this.currentTab === 'archive') {
          queryParams.append('status', 'COMPLETED');
        } else {
          queryParams.append('excludeStatus', 'COMPLETED');
        }
        if (this.currentTab === 'my' && this.myAdminId) {
          queryParams.append('assignedUserId', this.myAdminId);
        }
        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}tasks/filter-counts?${queryParams.toString()}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (requestId === this._taskCountsRequestId && response.data) {
          this.taskFilterCounts = { ...response.data };
        }
      } catch (error) {
        if (requestId === this._taskCountsRequestId) {
          console.error('Nie udało się pobrać liczników zadań:', error);
        }
      }
    },
    async loadUnreadTaskNotifications() {
      if (this._loadingTaskNotifications) return;
      this._loadingTaskNotifications = true;
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}notifications/user/unread-task-replies`,
          { headers }
        );
        const counts = response.data?.data || [];
        const grouped = {};
        counts.forEach((entry) => {
          if (!entry?.taskId || !entry?.count) return;
          grouped[entry.taskId] = Number(entry.count);
        });
        this.unreadTaskNotifications = grouped;
      } catch (error) {
        console.error('Nie udało się pobrać nieprzeczytanych odpowiedzi zadań:', error);
      } finally {
        this._loadingTaskNotifications = false;
      }
    },
    async markTaskNotificationsRead(taskId) {
      const unreadCount = Number(this.unreadTaskNotifications?.[taskId] || 0);
      if (!unreadCount) return;
      this.$delete(this.unreadTaskNotifications, taskId);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      try {
        await axios.patch(
          `${process.env.VUE_APP_ROOT_API}notifications/user/read-task/${encodeURIComponent(taskId)}`,
          {},
          { headers }
        );
        this.$root.$emit('notifications-updated');
      } catch (error) {
        this.$set(this.unreadTaskNotifications, taskId, unreadCount);
        this.loadUnreadTaskNotifications();
      }
    },
    async loadMyPermissions() {
      // Pobiera prawo `canAssignTasks` bieżącego admina (nie ma go w JWT/localStorage).
      try {
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'company-admins/me', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.myCanAssignTasks = !!(res.data && res.data.data && res.data.data.canAssignTasks);
      } catch (e) {
        this.myCanAssignTasks = false;
      }
    },
    async openAssignDialog() {
      const task = (this.tasks || []).find(t => t.id === this.currentTaskId);
      this.openAssignFromRow(task);
    },
    async openAssignFromRow(task) {
      if (!task) return;
      this.currentTaskId = task.id;
      const priority = task.priority || 'MEDIUM';
      this.assignForm = {
        assignedUserId: task.assignedUserId || null,
        // Termin: jeśli był ustawiony — zachowaj; inaczej wylicz z priorytetu
        dueDate: task.dueDate
          ? new Date(task.dueDate).toISOString().slice(0, 10)
          : this.dueDateFromPriority(priority),
        priority,
      };
      // Podgląd zgłoszenia — żeby wiedzieć KOMU przypisać zanim się przypisze.
      this.assignTaskTitle = task.title || '';
      this.assignTaskDescription = task.description || '';
      this.assignTaskDriver = task.driverCreatedBy
        ? `${task.driverCreatedBy.firstName || ''} ${task.driverCreatedBy.lastName || ''}`.trim()
        : '';
      this.assignTaskNotes = [];
      this.assignDialog = true;
      this.loadAssignPreview(task.id);
      if (this.adminsList.length === 0) {
        await this.loadAdmins();
      }
    },
    async loadAssignPreview(taskId) {
      this.loadingAssignPreview = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_ROOT_API}tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.data?.description) this.assignTaskDescription = res.data.description;
        if (res.data?.driverCreatedBy && !this.assignTaskDriver) {
          this.assignTaskDriver = `${res.data.driverCreatedBy.firstName || ''} ${res.data.driverCreatedBy.lastName || ''}`.trim();
        }
        this.assignTaskNotes = res.data?.notes || [];
      } catch (e) {
        this.assignTaskNotes = [];
      }
      this.loadingAssignPreview = false;
    },
    dueDateFromPriority(priority) {
      const offsets = { CRITICAL: 1, HIGH: 1, MEDIUM: 2, LOW: 3 };
      const days = offsets[priority] || 2;
      const d = new Date();
      d.setDate(d.getDate() + days);
      d.setHours(0, 0, 0, 0);
      // YYYY-MM-DD bez sliczania UTC
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    onPriorityChange() {
      // Po zmianie priorytetu auto-przelicz termin
      this.assignForm.dueDate = this.dueDateFromPriority(this.assignForm.priority);
    },
    async openReplyFromRow(task) {
      if (!task) return;
      this.replyTaskId = task.id;
      this.replyTaskTitle = task.title || '';
      this.replyTaskDescription = task.description || '';
      this.replyText = '';
      this.replyAttachment = [];
      this.replyNotes = [];
      this.replyDialog = true;
      // Dociągnij pełną treść zgłoszenia + wiadomości, żeby admin widział na co odpisuje.
      const loaded = await this.loadReplyPreview(task.id);
      if (loaded && this.replyDialog && this.replyTaskId === task.id) {
        await this.markTaskNotificationsRead(task.id);
      }
    },
    async loadReplyPreview(taskId) {
      const requestId = (this._replyPreviewRequestId || 0) + 1;
      this._replyPreviewRequestId = requestId;
      this.loadingReplyNotes = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_ROOT_API}tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (requestId !== this._replyPreviewRequestId) return false;
        if (res.data?.description) this.replyTaskDescription = res.data.description;
        this.replyNotes = res.data?.notes || [];
        this.replyNotes.forEach((n) => {
          if (n.attachmentFileId && this.isImageMime(n.attachmentMime) && !this.attachmentUrls[n.attachmentFileId]) {
            this.loadAttachment(n.attachmentFileId, n.attachmentMime);
          }
        });
        return true;
      } catch (e) {
        if (requestId === this._replyPreviewRequestId) this.replyNotes = [];
        return false;
      } finally {
        if (requestId === this._replyPreviewRequestId) this.loadingReplyNotes = false;
      }
    },
    openCompleteFromRow(task) {
      if (!task) return;
      this.completeTaskId = task.id;
      this.completeTaskTitle = task.title || '';
      this.completeNote = '';
      this.completeDialog = true;
    },
    async openPreviewFromRow(task) {
      if (!task) return;
      // Dane z wiersza od razu, pełne szczegóły (notatki, completedAt) dociągane niżej.
      this.previewTask = { ...task };
      this.previewNotes = [];
      this.previewReply = '';
      this.previewDialog = true;
      const loaded = await this.loadPreview(task.id);
      if (loaded && this.previewDialog && this.previewTask.id === task.id) {
        await this.markTaskNotificationsRead(task.id);
      }
    },
    async sendPreviewReply() {
      const content = (this.previewReply || '').trim();
      if (!content || !this.previewTask?.id) return;
      this.sendingPreviewReply = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_ROOT_API}tasks/${this.previewTask.id}/notes`,
          { content },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        this.previewReply = '';
        await this.loadPreview(this.previewTask.id);
      } catch (e) {
        console.error('sendPreviewReply:', e);
        this.showErrorMessage(e.response?.data?.message?.toString() || 'Nie udało się wysłać odpowiedzi');
      }
      this.sendingPreviewReply = false;
    },
    // Wejście z powiadomienia (?taskId=…) — otwórz podgląd bez czekania na listę.
    async openTaskById(taskId) {
      this.previewTask = { id: taskId };
      this.previewNotes = [];
      this.previewReply = '';
      this.previewDialog = true;
      const loaded = await this.loadPreview(taskId);
      if (loaded && this.previewDialog && this.previewTask.id === taskId) {
        await this.markTaskNotificationsRead(taskId);
      }
      // Zdejmij taskId z URL, żeby odświeżenie nie otwierało ponownie.
      if (this.$route.query.taskId) {
        this.$router.replace({ name: 'TaskPageVue', query: {} }).catch(() => {});
      }
    },
    async loadPreview(taskId) {
      const requestId = (this._previewRequestId || 0) + 1;
      this._previewRequestId = requestId;
      this.loadingPreview = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_ROOT_API}tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (requestId !== this._previewRequestId) return false;
        if (res.data) {
          this.previewTask = res.data;
          this.previewNotes = res.data.notes || [];
          this.previewNotes.forEach((n) => {
            if (n.attachmentFileId && this.isImageMime(n.attachmentMime) && !this.attachmentUrls[n.attachmentFileId]) {
              this.loadAttachment(n.attachmentFileId, n.attachmentMime);
            }
          });
          this.$nextTick(() => {
            const thread = this.$refs.previewThread;
            if (thread) thread.scrollTop = thread.scrollHeight;
          });
          return true;
        }
        return false;
      } catch (e) {
        if (requestId === this._previewRequestId) this.previewNotes = [];
        return false;
      } finally {
        if (requestId === this._previewRequestId) this.loadingPreview = false;
      }
    },
    async submitReply() {
      if (!this.replyTaskId) return;
      const files = this.replyAttachment || [];
      if (!this.replyText.trim() && !files.length) return;
      this.submittingReply = true;
      try {
        const headers = { Authorization: 'Bearer ' + localStorage.getItem('token') };
        if (files.length) {
          // Jeden plik = jeden wpis; treść (jeśli jest) przy pierwszym.
          for (let i = 0; i < files.length; i++) {
            const fd = new FormData();
            fd.append('file', files[i], files[i].name);
            fd.append('content', i === 0 ? this.replyText.trim() : '');
            await axios.post(
              process.env.VUE_APP_ROOT_API + `tasks/${this.replyTaskId}/notes/file`,
              fd,
              { headers },
            );
          }
        } else {
          await axios.post(
            process.env.VUE_APP_ROOT_API + `tasks/${this.replyTaskId}/notes`,
            { content: this.replyText.trim() },
            { headers },
          );
        }
        this.replyDialog = false;
        this.replyText = '';
        this.replyAttachment = [];
        await this.loadTasks();
        this.showTaskToast('Odpowiedź została wysłana', 'success');
      } catch (e) {
        this.showErrorMessage(e.response?.data?.message || 'Nie udało się wysłać odpowiedzi');
      }
      this.submittingReply = false;
    },
    async submitComplete() {
      if (!this.completeTaskId || !this.completeNote.trim()) return;
      this.submittingComplete = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + `tasks/${this.completeTaskId}/complete`,
          { completionNote: this.completeNote.trim() },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.completeDialog = false;
        this.completeNote = '';
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()]);
        this.showTaskToast('Zadanie zostało zakończone', 'success');
      } catch (e) {
        this.showErrorMessage(e.response?.data?.message || 'Nie udało się zakończyć zadania');
      }
      this.submittingComplete = false;
    },
    async loadAdmins() {
      this.loadingAdmins = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'company-admins',
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        const list = res.data?.data?.result || res.data?.data || [];
        this.adminsList = Array.isArray(list) ? list : [];
      } catch (e) {
        this.adminsList = [];
      }
      this.loadingAdmins = false;
    },
    async submitAssign() {
      if (!this.currentTaskId) return;
      this.submittingAssign = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + `tasks/${this.currentTaskId}/assign`,
          {
            assignedUserId: this.assignForm.assignedUserId || null,
            dueDate: this.assignForm.dueDate || null,
            priority: this.assignForm.priority || 'MEDIUM',
          },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.assignDialog = false;
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()]);
        this.showTaskToast(this.assignForm.assignedUserId ? 'Zadanie zostało przypisane' : 'Usunięto przypisanie zadania', 'success');
      } catch (e) {
        const msg = e.response?.data?.message || 'Nie udało się przypisać zadania';
        this.showErrorMessage(msg.toString());
      }
      this.submittingAssign = false;
    },
    async loadTasks() {
      const requestId = (this._taskRequestId || 0) + 1
      this._taskRequestId = requestId
      this.loading = true
      try {
        const { page, itemsPerPage } = this.options
        const queryParams = new URLSearchParams({
          page: page || 1,
          limit: itemsPerPage || 10
        })

        if (this.search) {
          queryParams.append('search', this.search)
        }
        if (this.quickFilter && this.quickFilter !== 'all') {
          queryParams.append('filter', this.quickFilter)
        }

        // Sortowanie z tabeli (serwerowe — tabela ma server-items-length)
        const sortBy = this.options.sortBy && this.options.sortBy[0]
        if (sortBy) {
          queryParams.append('sortBy', sortBy)
          queryParams.append('sortOrder', this.options.sortDesc && this.options.sortDesc[0] ? 'desc' : 'asc')
        }

        if (this.currentTab === 'archive') {
          queryParams.append('status', 'COMPLETED')
        } else {
          queryParams.append('excludeStatus', 'COMPLETED')
        }

        // Podzakładka „Moje" — tylko zadania przypisane do zalogowanego
        if (this.currentTab === 'my' && this.myAdminId) {
          queryParams.append('assignedUserId', this.myAdminId)
        }

        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}tasks?${queryParams.toString()}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )

        if (requestId !== this._taskRequestId) return
        if (response.data) {
          this.tasks = response.data.data || []
          this.totalItems = response.data.meta?.total || 0
        }
      } catch (error) {
        if (requestId !== this._taskRequestId) return
        console.error('Error loading tasks:', error)
        // Fallback to empty array on error
        this.tasks = []
        this.totalItems = 0
      } finally {
        if (requestId === this._taskRequestId) this.loading = false
      }
    },

    async loadUsers() {
      this.loadingUsers = true
      try {
        const response = await axios.get(
          process.env.VUE_APP_ROOT_API + 'company-admins',
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )
        const users = response.data?.data?.result || response.data?.data || []
        this.users = (Array.isArray(users) ? users : []).map(user => ({
          id: user.id,
          fullName: `${user.firstName} ${user.lastName}`,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        }))
      } catch (error) {
        console.error('Error loading users:', error)
        this.users = []
      }
      this.loadingUsers = false
    },

    async loadLinkedRecords() {
      if (!this.formData.linkedRecordType) return

      this.loadingLinkedRecords = true
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}${this.formData.linkedRecordType}?take=100&skip=0`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )

        this.linkedRecords = response.data.data?.map(record => ({
          id: record.id,
          name: `${this.formData.linkedRecordType.toUpperCase()} #${record.id}`,
          ...record
        })) || []
      } catch (error) {
        console.error('Error loading linked records:', error)
        this.linkedRecords = []
      }
      this.loadingLinkedRecords = false
    },

    async loadStatistics() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}tasks/statistics`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )

        const stats = response.data
        this.taskSummary = {
          total: stats.total || 0,
          new: stats.byStatus?.NEW || 0,
          inProgress: stats.byStatus?.IN_PROGRESS || 0,
          completed: stats.byStatus?.COMPLETED || 0,
          avgTime: this.formatDuration(stats.avgCompletionMinutes)
        }
        this.statistics = [
          { label: 'Wszystkie', value: stats.total, color: 'blue' },
          { label: 'Nowe', value: stats.byStatus?.NEW || 0, color: 'orange' },
          { label: 'W trakcie', value: stats.byStatus?.IN_PROGRESS || 0, color: 'purple' },
          { label: 'Zakończone', value: stats.byStatus?.COMPLETED || 0, color: 'green' },
          { label: 'Śr. czas / zadanie', value: this.formatDuration(stats.avgCompletionMinutes), color: 'teal' }
        ]

        if (stats.userStats) {
          this.userStatistics = stats.userStats.map(u => ({
            ...u,
            avgTime: this.formatDuration(u.avgCompletionMinutes),
          }))
        }
      } catch (error) {
        console.error('Error loading statistics:', error)
        this.taskSummary = { total: 0, new: 0, inProgress: 0, completed: 0, avgTime: '—' }
        // Fallback to default statistics
        this.statistics = [
          { label: 'Wszystkie', value: 0, color: 'blue' },
          { label: 'Nowe', value: 0, color: 'orange' },
          { label: 'W trakcie', value: 0, color: 'purple' },
          { label: 'Zakończone', value: 0, color: 'green' }
        ]
      }
    },

    formatDuration(minutes) {
      if (minutes == null) return '—'
      if (minutes < 1) return '<1 min'
      if (minutes < 60) return `${minutes} min`
      const h = Math.floor(minutes / 60)
      const m = minutes % 60
      if (h < 24) return m ? `${h}h ${m}min` : `${h}h`
      const d = Math.floor(h / 24)
      const rh = h % 24
      return rh ? `${d}d ${rh}h` : `${d}d`
    },
    editTask(id) {
      const task = this.tasks.find(t => t.id === id)
      if (task) {
        this.currentTaskId = id
        this.formData = {
          title: task.title,
          description: task.description,
          type: task.type,
          status: task.status,
          priority: task.priority || 'MEDIUM',
          assignedUserId: task.assignedUser?.id || null,
          dueDate: task.dueDate,
          linkedRecordType: task.linkedRecord?.type || null,
          linkedRecordId: task.linkedRecord?.id || null
        }
        if (this.formData.linkedRecordType) {
          this.loadLinkedRecords()
        }
        this.loadTaskNotes()
        this.dialog = true
      }
    },

    // Wyróżnienie wiersza dla podbitych (ważnych) zadań.
    rowClass(item) {
      return item && item.important ? 'task-important-row' : '';
    },
    // Podbij / cofnij podbicie zadania jako ważne.
    async toggleImportant(item) {
      try {
        await axios.put(
          `${process.env.VUE_APP_ROOT_API}tasks/${item.id}/toggle-important`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )
        await this.loadTasks()
        this.showTaskToast(item.important ? 'Usunięto oznaczenie „ważne”' : 'Zadanie oznaczone jako ważne', 'success')
      } catch (error) {
        console.error('Error toggling important:', error)
        this.showErrorMessage('Błąd podczas podbijania zadania: ' + (error.response?.data?.message || error.message))
      }
    },

    async deleteTask(id) {
      const task = (this.tasks || []).find(item => item.id === id)
      const result = await Swal.fire({
        title: 'Usunąć zadanie?',
        text: task?.title ? `„${task.title}” zostanie przeniesione poza aktywną listę.` : 'Zadanie zostanie przeniesione poza aktywną listę.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Zostaw zadanie',
        reverseButtons: true,
        buttonsStyling: false,
        background: '#1b2130',
        color: '#f5f7fc',
        customClass: {
          popup: 'btp-task-feedback',
          confirmButton: 'btp-task-confirm-btn btp-task-confirm-btn--danger',
          cancelButton: 'btp-task-confirm-cancel'
        }
      })
      if (!result.isConfirmed) return
      try {
        await axios.delete(
          `${process.env.VUE_APP_ROOT_API}tasks/${id}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()])
        if (this.previewTask?.id === id) this.previewDialog = false
        this.showTaskToast('Zadanie zostało usunięte', 'success')
      } catch (error) {
        console.error('Error deleting task:', error)
        this.showErrorMessage('Błąd podczas usuwania zadania: ' + (error.response?.data?.message || error.message))
      }
    },

    async submitForm() {
      if (!this.valid) return

      this.submitting = true
      try {
        const taskData = { ...this.formData }

        // Usuń puste stringi / null — DTO ma @IsOptional, ale walidatory @IsUUID / @IsDateString
        // odrzucają "" i null. Wysyłaj tylko pola z wartością.
        Object.keys(taskData).forEach(k => {
          const w = taskData[k]
          if (w === '' || w === null || w === undefined || (Array.isArray(w) && !w.length)) {
            delete taskData[k]
          }
        })
        // Przy edycji nie wysylamy listy osob — edytujemy jedno konkretne zadanie.
        if (this.currentTaskId) delete taskData.assignedUserIds
        // Przy tworzeniu lista ma pierwszenstwo; nie wysylamy obu naraz.
        if (!this.currentTaskId && taskData.assignedUserIds) delete taskData.assignedUserId

        // companyId nie jest w DTO — backend pobiera go z JWT przez @User()
        delete taskData.companyId

        // V-date-picker zwraca "YYYY-MM-DD" — konwertuj na pełny ISO, którego oczekuje Prisma
        if (taskData.dueDate && /^\d{4}-\d{2}-\d{2}$/.test(taskData.dueDate)) {
          taskData.dueDate = new Date(taskData.dueDate + 'T00:00:00.000Z').toISOString()
        }

        let savedTaskId = this.currentTaskId
        let utworzoneId = []
        if (this.currentTaskId) {
          // Update existing task
          await axios.put(
            `${process.env.VUE_APP_ROOT_API}tasks/${this.currentTaskId}`,
            taskData,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }
          )
        } else {
          // Create new task
          const createRes = await axios.post(
            `${process.env.VUE_APP_ROOT_API}tasks`,
            taskData,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }
          )
          const zwrocone = createRes.data?.data ?? createRes.data
          // Przy kilku osobach backend zwraca TABLICE zadan — po jednym na osobe.
          utworzoneId = Array.isArray(zwrocone)
            ? zwrocone.map(z => z?.id).filter(Boolean)
            : [zwrocone?.id].filter(Boolean)
          savedTaskId = utworzoneId[0] || null
        }

        // Screenshoty dodane w formularzu → wpisy z załącznikiem w wątku zadania.
        // Przy kilku osobach ten sam załącznik ląduje w każdym z zadań, bo każde
        // jest osobne i czytane przez inną osobę.
        const doZalacznikow = this.currentTaskId
          ? (savedTaskId ? [savedTaskId] : [])
          : utworzoneId
        if (doZalacznikow.length && this.pendingAttachments.length) {
          for (const id of doZalacznikow) {
            for (const att of this.pendingAttachments) {
              const fd = new FormData()
              fd.append('file', att.file, att.file.name)
              fd.append('content', '')
              await axios.post(
                `${process.env.VUE_APP_ROOT_API}tasks/${id}/notes/file`,
                fd,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
              )
            }
          }
        }

        const wasEditing = !!this.currentTaskId
        this.closeDialog()
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()])
        const ile = utworzoneId.length
        this.showTaskToast(
          wasEditing
            ? 'Zmiany zostały zapisane'
            : (ile > 1 ? `Utworzono ${ile} zadań — po jednym dla każdej osoby` : 'Nowe zadanie zostało utworzone'),
          'success',
        )
      } catch (error) {
        console.error('Error submitting task:', error)
        // Show error message to user
        this.showErrorMessage('Błąd podczas zapisywania zadania: ' + (error.response?.data?.message || error.message))
      }
      this.submitting = false
    },

    closeDialog() {
      this.dialog = false
      this.currentTaskId = null
      this.formData = {
        title: '',
        description: '',
        type: 'INTERNAL',
        status: 'NEW',
        priority: 'MEDIUM',
        assignedUserId: null,
        assignedUserIds: [],
        dueDate: null,
        linkedRecordType: null,
        linkedRecordId: null
      }
      this.linkedRecords = []
      this.taskNotes = []
      this.newNote = ''
      this.pendingAttachments.forEach((a) => a.preview && URL.revokeObjectURL(a.preview))
      this.pendingAttachments = []
      this.$refs.form?.reset()
    },

    onTypeChange() {
      if (this.formData.type === 'INTERNAL') {
        this.formData.linkedRecordType = null
        this.formData.linkedRecordId = null
        this.linkedRecords = []
      }
    },

    async changeStatus(item, newStatus) {
      if (item.status === newStatus) return
      if (newStatus === 'COMPLETED') {
        this.openCompleteFromRow(item)
        return
      }
      try {
        await axios.put(
          `${process.env.VUE_APP_ROOT_API}tasks/${item.id}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()])
        this.showTaskToast(`Status zmieniony na: ${this.getStatusText(newStatus)}`, 'success')
      } catch (error) {
        console.error('Error changing status:', error)
        this.showErrorMessage('Błąd zmiany statusu: ' + (error.response?.data?.message || error.message))
      }
    },
    getStatusColor(status) {
      const colors = {
        'NEW': 'blue',
        'IN_PROGRESS': 'orange',
        'COMPLETED': 'green',
        'CANCELLED': 'red'
      }
      return colors[status] || 'grey'
    },

    getStatusText(status) {
      const texts = {
        'NEW': 'Nowe',
        'IN_PROGRESS': 'W trakcie',
        'COMPLETED': 'Zakończone',
        'CANCELLED': 'Anulowane'
      }
      return texts[status] || status
    },

    getPriorityColor(priority) {
      const colors = {
        'LOW': 'green',
        'MEDIUM': 'orange',
        'HIGH': 'deep-orange',
        'CRITICAL': 'red'
      }
      return colors[priority] || 'grey'
    },

    getPriorityText(priority) {
      const texts = {
        'LOW': 'Niski',
        'MEDIUM': 'Średni',
        'HIGH': 'Wysoki',
        'CRITICAL': 'Krytyczny'
      }
      return texts[priority] || priority
    },

    async toggleTaskStatus(taskId) {
      try {
        await axios.put(
          `${process.env.VUE_APP_ROOT_API}tasks/${taskId}/toggle-status`,
          {},
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )
        // Pełne przeładowanie — bezpośrednie przypisanie do tasks[idx] nie jest reaktywne w Vue 2
        await Promise.all([this.loadTasks(), this.loadStatistics(), this.loadTaskFilterCounts()])
      } catch (error) {
        console.error('Error toggling task status:', error)
        this.showErrorMessage('Błąd podczas zmiany statusu zadania: ' + (error.response?.data?.message || error.message))
      }
    },

    async loadTaskNotes() {
      if (!this.currentTaskId) return

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_ROOT_API}tasks/${this.currentTaskId}`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        )
        this.taskNotes = response.data?.notes || []
        // Pre-fetch obrazki w załącznikach (best-effort)
        this.taskNotes.forEach(n => {
          if (n.attachmentFileId && this.isImageMime(n.attachmentMime) && !this.attachmentUrls[n.attachmentFileId]) {
            this.loadAttachment(n.attachmentFileId, n.attachmentMime)
          }
        })
      } catch (error) {
        console.error('Error loading task notes:', error)
        this.taskNotes = []
      }
    },

    async addNote() {
      const files = this.newNoteAttachment || []
      if ((!this.newNote.trim() && !files.length) || !this.currentTaskId) return

      this.addingNote = true
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` }
        if (files.length) {
          // Jeden plik = jeden wpis w wątku; treść (jeśli jest) przy pierwszym.
          const responses = []
          for (let i = 0; i < files.length; i++) {
            const fd = new FormData()
            fd.append('file', files[i], files[i].name)
            fd.append('content', i === 0 ? this.newNote.trim() : '')
            const r = await axios.post(
              `${process.env.VUE_APP_ROOT_API}tasks/${this.currentTaskId}/notes/file`,
              fd,
              { headers },
            )
            responses.push(r.data)
          }
          this.taskNotes.unshift(...responses)
        } else {
          const r = await axios.post(
            `${process.env.VUE_APP_ROOT_API}tasks/${this.currentTaskId}/notes`,
            { content: this.newNote.trim() },
            { headers },
          )
          this.taskNotes.unshift(r.data)
        }
        this.newNote = ''
        this.newNoteAttachment = []
        this.showTaskToast('Wiadomość została dodana', 'success')
      } catch (error) {
        console.error('Error adding note:', error)
        this.showErrorMessage('Błąd podczas dodawania notatki: ' + (error.response?.data?.message || error.message))
      }
      this.addingNote = false
    },

    isImageMime(mime) {
      return !!(mime && mime.startsWith('image/'))
    },
    addPendingFile(file) {
      if (!file) return
      const entry = { file, preview: null }
      if (file.type && file.type.startsWith('image/')) {
        entry.preview = URL.createObjectURL(file)
      }
      this.pendingAttachments.push(entry)
    },
    onTaskFilesPicked(e) {
      for (const f of e.target.files || []) this.addPendingFile(f)
      e.target.value = ''
    },
    onTaskPaste(e) {
      const items = e.clipboardData && e.clipboardData.items
      if (!items) return
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile()
          if (f) {
            // wklejony screenshot ma nazwę "image.png" — nadaj czytelną
            const named = new File([f], `screenshot-${Date.now()}.png`, { type: f.type })
            this.addPendingFile(named)
          }
        }
      }
    },
    removePendingAttachment(i) {
      const [rm] = this.pendingAttachments.splice(i, 1)
      if (rm && rm.preview) URL.revokeObjectURL(rm.preview)
    },
    async loadAttachment(fileId, mime) {
      if (!fileId || this.attachmentUrls[fileId]) return
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_ROOT_API}aws/download-file/${fileId}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' },
        )
        const blob = mime ? new Blob([response.data], { type: mime }) : response.data
        const url = URL.createObjectURL(blob)
        this.$set(this.attachmentUrls, fileId, url)
      } catch (e) {
        console.error('Failed to load attachment', e)
      }
    },
    async openAttachmentInNewTab(fileId) {
      if (!this.attachmentUrls[fileId]) await this.loadAttachment(fileId)
      const url = this.attachmentUrls[fileId]
      if (url) window.open(url, '_blank')
    },
    async downloadAttachment(fileId, name) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_ROOT_API}aws/download-file/${fileId}`,
          {},
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob' },
        )
        const url = URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = name || 'zalacznik'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (e) {
        console.error('Failed to download', e)
      }
    },

    formatDateOnly(dateString) {
      if (!dateString) return '—'
      return new Date(dateString).toLocaleDateString('pl-PL')
    },
    // Czas od utworzenia do zakończenia, np. "2 dni 4 godz." / "3 godz. 15 min" / "42 min".
    formatElapsed(start, end) {
      const ms = new Date(end) - new Date(start)
      if (!isFinite(ms) || ms < 0) return '—'
      const min = Math.round(ms / 60000)
      if (min < 60) return `${min} min`
      const h = Math.floor(min / 60)
      if (h < 24) return `${h} godz.${min % 60 ? ' ' + (min % 60) + ' min' : ''}`
      const d = Math.floor(h / 24)
      return `${d} ${d === 1 ? 'dzień' : 'dni'}${h % 24 ? ' ' + (h % 24) + ' godz.' : ''}`
    },
    formatNoteDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    chronologicalNotes(notes) {
      return [...(notes || [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
    },
    // 3 rodzaje not: system (auto-reply, brak authorId+driverAuthorId) / admin / driver
    noteRole(note) {
      if (note.authorId) return 'admin';
      if (note.driverAuthorId) return 'driver';
      return 'system';
    },
    noteRoleClass(note) {
      const r = this.noteRole(note);
      return r === 'admin' ? 'admin-note' : r === 'driver' ? 'driver-note' : 'system-note';
    },
    noteRoleStyle(note) {
      const r = this.noteRole(note);
      // Ciemne tła pod ciemny motyw panelu (wcześniej jasne → biały tekst niewidoczny).
      if (r === 'admin') return { borderLeftColor: '#2962FF', backgroundColor: 'rgba(41,98,255,0.14)' };
      if (r === 'driver') return { borderLeftColor: '#4caf50', backgroundColor: 'rgba(76,175,80,0.14)' };
      // system (auto-reply)
      return { borderLeftColor: '#9e9e9e', backgroundColor: 'rgba(158,158,158,0.14)' };
    },
    noteIcon(note) {
      const r = this.noteRole(note);
      if (r === 'admin') return 'mdi-account-tie';
      if (r === 'driver') return 'mdi-account';
      return 'mdi-robot-outline';
    },
    noteIconColor(note) {
      const r = this.noteRole(note);
      if (r === 'admin') return 'blue';
      if (r === 'driver') return 'green';
      return 'grey';
    },
    noteAuthorLabel(note) {
      const r = this.noteRole(note);
      if (r === 'admin') {
        return `${note.author?.firstName || ''} ${note.author?.lastName || ''} (Admin)`.trim();
      }
      if (r === 'driver') {
        return `${note.driverAuthor?.firstName || ''} ${note.driverAuthor?.lastName || ''} (${this.$t('common.driver')})`.trim();
      }
      return 'BTP — automatyczna odpowiedź';
    },
    nextStatusLabel(status) {
      const map = { NEW: 'W trakcie', IN_PROGRESS: 'Zakończone', COMPLETED: 'Nowe' }
      return map[status] || 'Nowe'
    },
    dueDateMeta(dateString) {
      const d = new Date(dateString)
      const now = new Date()
      const dayMs = 24 * 60 * 60 * 1000
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const target = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      const diff = Math.round((target - start) / dayMs)

      const months = ['sty','lut','mar','kwi','maj','cze','lip','sie','wrz','paź','lis','gru']
      const dateLabel = `${d.getDate()} ${months[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? ' ' + d.getFullYear() : ''}`

      let color = 'blue-grey lighten-5'
      let textColor = 'blue-grey darken-3'
      let icon = 'mdi-calendar-blank-outline'
      let relLabel = ''

      if (diff < 0) {
        color = 'red lighten-5'; textColor = 'red darken-3'; icon = 'mdi-calendar-alert'
        const n = Math.abs(diff)
        relLabel = n === 1 ? ' · wczoraj' : ` · ${n} dni temu`
      } else if (diff === 0) {
        color = 'orange lighten-5'; textColor = 'orange darken-4'; icon = 'mdi-calendar-today'
        relLabel = ' · dziś'
      } else if (diff === 1) {
        color = 'amber lighten-5'; textColor = 'amber darken-4'; icon = 'mdi-calendar-arrow-right'
        relLabel = ' · jutro'
      } else if (diff <= 7) {
        color = 'green lighten-5'; textColor = 'green darken-3'; icon = 'mdi-calendar-check-outline'
        relLabel = ` · za ${diff} dni`
      } else {
        color = 'blue-grey lighten-5'; textColor = 'blue-grey darken-3'; icon = 'mdi-calendar-blank-outline'
        relLabel = ` · za ${diff} dni`
      }

      return { dateLabel, relLabel, color, textColor, icon }
    },

    showTaskToast(message, icon = 'success') {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon,
        title: message,
        showConfirmButton: false,
        timer: 2600,
        timerProgressBar: true,
        background: '#202737',
        color: '#f5f7fc',
        customClass: { popup: 'btp-task-toast' }
      })
    },
    showErrorMessage(message) {
      console.error(message)
      Swal.fire({
        title: 'Nie udało się wykonać operacji',
        text: String(message || 'Spróbuj ponownie za chwilę.'),
        icon: 'error',
        confirmButtonText: 'Rozumiem',
        buttonsStyling: false,
        background: '#1b2130',
        color: '#f5f7fc',
        customClass: {
          popup: 'btp-task-feedback',
          confirmButton: 'btp-task-confirm-btn'
        }
      })
    }
  }
}
</script>

<style scoped>
.tasks-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary { background: #5478FF; color: #FFFFFF; }
.action-pill--primary:hover { background: #3F62E8; }
.action-pill--secondary {
  background: rgba(148, 160, 184, 0.15);
  color: #E6EAF2;
  border-color: #2A3142;
}
.action-pill--secondary:hover { background: rgba(148, 160, 184, 0.25); }
.tasks-page >>> .v-tab {
  color: #94A0B8 !important;
}
.tasks-page >>> .v-tab--active {
  color: #5478FF !important;
}
.tasks-page >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.tasks-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.tasks-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.tasks-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.tasks-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
/* Podbite (ważne) zadania — wyróżnienie: żółta lewa krawędź + delikatne tło */
.tasks-page >>> .v-data-table tbody tr.task-important-row {
  background: rgba(255, 193, 7, 0.10) !important;
  box-shadow: inset 3px 0 0 #FFC107;
}
.tasks-page >>> .v-data-table tbody tr.task-important-row:hover {
  background: rgba(255, 193, 7, 0.16) !important;
}
.tasks-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.tasks-page >>> .v-text-field input,
.tasks-page >>> .v-text-field textarea,
.tasks-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.tasks-page >>> .v-label {
  color: #94A0B8 !important;
}
.tasks-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.tasks-page >>> .v-card__title,
.tasks-page >>> .v-card__text {
  color: #E6EAF2 !important;
}

.admin-note, .driver-note, .system-note {
  color: #E8EAF6 !important;
}
.admin-note strong, .driver-note strong, .system-note strong,
.admin-note small, .driver-note small, .system-note small,
.admin-note .grey--text, .driver-note .grey--text, .system-note .grey--text {
  color: #C7CBD6 !important;
}
.note-body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  color: #E8EAF6 !important;
  font-weight: 500;
  letter-spacing: 0.005em;
}
.task-author-chip {
  font-weight: 500;
  letter-spacing: 0.02em;
}
.due-chip {
  font-weight: 500;
  font-size: 12px !important;
  letter-spacing: 0.01em;
}
.due-chip-date { font-weight: 600; }
.due-chip-rel { opacity: 0.75; font-weight: 400; margin-left: 2px; }
.status-chip-clickable {
  cursor: pointer !important;
  transition: transform .12s, box-shadow .12s;
}
.status-chip-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
}

/* === Zadania 2.0 — operacyjny workspace === */
.tasks-page {
  --task-bg: #171b27;
  --task-panel: #202635;
  --task-panel-soft: #252c3d;
  --task-border: rgba(255, 255, 255, 0.085);
  --task-text: #f5f7fc;
  --task-muted: #9ca8bd;
  --task-primary: #5b7cff;
  --task-green: #42d787;
  --task-amber: #ffbd66;
  --task-red: #ff7373;
  --task-violet: #a98aff;
  margin: -24px;
  padding: 24px;
  background:
    radial-gradient(circle at 78% -8%, rgba(91, 124, 255, 0.1), transparent 34%),
    var(--task-bg);
  min-height: calc(100vh - 64px);
}

.tasks-hero {
  position: relative;
  overflow: hidden;
  max-width: 1560px;
  margin: 0 auto 18px;
  padding: 26px;
  border: 1px solid var(--task-border);
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(37, 44, 61, 0.98), rgba(27, 33, 47, 0.98));
  box-shadow: 0 20px 55px rgba(5, 8, 18, 0.22);
}
.tasks-hero__glow {
  position: absolute;
  width: 420px;
  height: 420px;
  right: -120px;
  top: -250px;
  border-radius: 50%;
  background: rgba(91, 124, 255, 0.2);
  filter: blur(4px);
  pointer-events: none;
}
.tasks-hero__top,
.tasks-hero__identity,
.tasks-hero__actions,
.tasks-metrics,
.task-metric,
.task-tabs,
.tasks-toolbar__controls,
.task-card__badges,
.task-card__meta,
.task-person,
.task-meta-item,
.task-card__actions,
.tasks-pagination {
  display: flex;
  align-items: center;
}
.tasks-hero__top {
  position: relative;
  justify-content: space-between;
  gap: 24px;
}
.tasks-hero__identity { align-items: flex-start; gap: 16px; }
.tasks-hero__icon {
  display: grid;
  place-items: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border: 1px solid rgba(119, 147, 255, 0.26);
  border-radius: 14px;
  color: #8da4ff;
  background: linear-gradient(145deg, rgba(91, 124, 255, 0.2), rgba(91, 124, 255, 0.07));
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.08);
}
.tasks-hero__icon >>> .v-icon { color: inherit !important; }
.tasks-eyebrow {
  display: block;
  margin-bottom: 5px;
  color: #8fa6ff;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.tasks-hero h1 {
  margin: 0;
  color: var(--task-text);
  font-size: clamp(25px, 3vw, 34px);
  line-height: 1.08;
  letter-spacing: -0.025em;
}
.tasks-hero p {
  max-width: 660px;
  margin: 8px 0 0;
  color: var(--task-muted);
  font-size: 14px;
  line-height: 1.55;
}
.tasks-hero__actions { gap: 10px; }
.task-button,
.task-filter-toggle,
.task-tab,
.task-star,
.task-card__open,
.task-status,
.task-action {
  font: inherit;
  border: 0;
  cursor: pointer;
}
.task-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  gap: 8px;
  padding: 0 17px;
  border-radius: 11px;
  color: var(--task-text);
  font-size: 13px;
  font-weight: 750;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.task-button:hover { transform: translateY(-1px); }
.task-button--primary {
  background: linear-gradient(135deg, #6685ff, #4f70f5);
  box-shadow: 0 10px 24px rgba(79, 112, 245, 0.24);
}
.task-button--ghost {
  border: 1px solid var(--task-border);
  background: rgba(255, 255, 255, 0.04);
}
.task-button--ghost:hover { border-color: rgba(126, 151, 255, 0.36); background: rgba(91, 124, 255, 0.09); }
.task-button >>> .v-icon { color: inherit !important; }

.tasks-metrics {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 24px;
}
.task-metric {
  min-width: 0;
  gap: 11px;
  padding: 13px 14px;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 14px;
  background: rgba(10, 14, 24, 0.2);
}
.task-metric__icon {
  display: grid;
  place-items: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(91, 124, 255, 0.13);
  color: #8ea5ff;
}
.task-metric__icon >>> .v-icon { color: inherit !important; }
.task-metric--amber .task-metric__icon { color: var(--task-amber); background: rgba(255, 189, 102, 0.12); }
.task-metric--green .task-metric__icon { color: var(--task-green); background: rgba(66, 215, 135, 0.11); }
.task-metric--violet .task-metric__icon { color: var(--task-violet); background: rgba(169, 138, 255, 0.11); }
.task-metric__copy { display: flex; min-width: 0; flex-direction: column; }
.task-metric__copy small { color: var(--task-muted); font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.task-metric__copy strong { color: var(--task-text); font-size: 19px; line-height: 1.2; }
.task-metric__hint { margin-left: auto; color: #7f8aa0; font-size: 10px; text-align: right; }

.tasks-workspace {
  max-width: 1560px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid var(--task-border);
  border-radius: 20px;
  background: rgba(30, 36, 50, 0.9);
  box-shadow: 0 18px 50px rgba(5, 8, 18, 0.16);
}
.tasks-toolbar {
  padding: 18px 20px;
  border-bottom: 1px solid var(--task-border);
  background: rgba(255, 255, 255, 0.012);
}
.task-tabs {
  gap: 6px;
  margin-bottom: 14px;
}
.task-tab {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  gap: 7px;
  padding: 0 13px;
  border: 1px solid transparent;
  border-radius: 10px;
  color: #929db2;
  background: transparent;
  font-size: 12px;
  font-weight: 700;
  transition: 0.18s ease;
}
.task-tab >>> .v-icon { color: inherit !important; }
.task-tab small {
  min-width: 21px;
  padding: 2px 6px;
  border-radius: 999px;
  color: #abb5c7;
  background: rgba(255, 255, 255, 0.07);
  font-size: 10px;
  text-align: center;
}
.task-tab:hover { color: #dce2ed; background: rgba(255, 255, 255, 0.035); }
.task-tab.is-active {
  border-color: rgba(91, 124, 255, 0.25);
  color: #aebeff;
  background: rgba(91, 124, 255, 0.12);
}
.task-tab.is-active small { color: #dfe5ff; background: rgba(91, 124, 255, 0.22); }
.tasks-toolbar__controls { gap: 10px; }
.task-search { flex: 1 1 420px; }
.task-sort { flex: 0 1 230px; }
.tasks-toolbar__controls >>> .v-input__slot,
.tasks-page-size >>> .v-input__slot {
  min-height: 42px !important;
  border-radius: 11px !important;
  background: rgba(11, 15, 25, 0.26) !important;
}
.tasks-toolbar__controls >>> fieldset,
.tasks-page-size >>> fieldset { border-color: rgba(255, 255, 255, 0.09) !important; }
.tasks-toolbar__controls >>> .v-input--is-focused fieldset { border-color: rgba(91, 124, 255, 0.75) !important; }
.tasks-toolbar__controls >>> input,
.tasks-toolbar__controls >>> .v-select__selection,
.tasks-page-size >>> .v-select__selection { color: var(--task-text) !important; font-size: 13px; }
.tasks-toolbar__controls >>> .v-icon,
.tasks-page-size >>> .v-icon { color: #8591a7 !important; }
.task-filter-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  gap: 7px;
  padding: 0 13px;
  border: 1px solid var(--task-border);
  border-radius: 11px;
  color: #9ba7ba;
  background: rgba(255, 255, 255, 0.025);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.task-filter-toggle >>> .v-icon { color: inherit !important; }
.task-filter-toggle span { min-width: 20px; padding: 1px 5px; border-radius: 999px; background: rgba(255,255,255,.07); font-size: 10px; }
.task-filter-toggle.is-active { border-color: rgba(66, 215, 135, 0.28); color: #75e5a8; background: rgba(66, 215, 135, 0.09); }

.tasks-results-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.055);
  color: var(--task-muted);
  font-size: 11px;
}
.tasks-results-bar > div { display: flex; align-items: baseline; gap: 7px; }
.tasks-results-bar strong { color: #dce2ed; font-size: 12px; }
.tasks-results-bar__filter { display: inline-flex; align-items: center; gap: 5px; color: #75e5a8; }
.tasks-results-bar__filter >>> .v-icon { color: inherit !important; }

.task-list { padding: 10px; }
.task-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  min-height: 190px;
  margin-bottom: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(39, 46, 63, 0.78), rgba(31, 37, 51, 0.84));
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  animation: taskCardEnter 0.32s both;
}
.task-card:nth-child(2) { animation-delay: 0.035s; }
.task-card:nth-child(3) { animation-delay: 0.07s; }
.task-card:nth-child(4) { animation-delay: 0.105s; }
.task-card:last-child { margin-bottom: 0; }
.task-card:hover { transform: translateY(-1px); border-color: rgba(119, 146, 255, 0.2); box-shadow: 0 14px 30px rgba(5, 8, 18, 0.16); }
.task-card__accent { position: absolute; inset: 18px auto 18px 0; width: 3px; border-radius: 0 4px 4px 0; background: #5b7cff; opacity: 0.72; }
.task-card.is-status-progress .task-card__accent { background: var(--task-amber); }
.task-card.is-status-done .task-card__accent { background: var(--task-green); }
.task-card.is-status-cancelled .task-card__accent { background: #7d879b; }
.task-card.is-overdue .task-card__accent { background: var(--task-red); }
.task-card.is-important { border-color: rgba(255, 189, 102, 0.2); }
.task-star {
  position: absolute;
  z-index: 2;
  top: 14px;
  right: 274px;
  display: grid;
  place-items: center;
  width: 33px;
  height: 33px;
  border-radius: 9px;
  color: #727e93;
  background: transparent;
  transition: color .16s ease, background .16s ease, transform .16s ease;
}
.task-star:hover { color: #ffcf7f; background: rgba(255, 189, 102, 0.08); transform: rotate(-8deg); }
.task-star.is-active { color: var(--task-amber); }
.task-star >>> .v-icon { color: inherit !important; }
.task-card__content { min-width: 0; padding: 20px 54px 18px 22px; }
.task-card__badges { flex-wrap: wrap; gap: 6px; margin-bottom: 11px; }
.task-badge {
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  gap: 5px;
  padding: 0 8px;
  border: 1px solid rgba(255,255,255,.07);
  border-radius: 999px;
  color: #aeb8ca;
  background: rgba(255,255,255,.04);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: .045em;
  text-transform: uppercase;
}
.task-badge >>> .v-icon { color: inherit !important; }
.task-badge--driver { border-color: rgba(66, 215, 135, 0.18); color: #75e5a8; background: rgba(66, 215, 135, 0.07); }
.task-badge--internal { color: #aebeff; background: rgba(91, 124, 255, 0.07); }
.task-badge--linked { color: #c2acef; background: rgba(169,138,255,.08); }
.task-badge--priority-low { color: #8bdcaf; }
.task-badge--priority-medium { color: #aeb8ca; }
.task-badge--priority-high { color: #ffbd66; border-color: rgba(255,189,102,.18); }
.task-badge--priority-critical { color: #ff8b8b; border-color: rgba(255,115,115,.22); background: rgba(255,115,115,.08); }
.task-card__open { display: block; width: 100%; padding: 0; color: inherit; text-align: left; background: transparent; }
.task-card__open h2 { max-width: 850px; margin: 0; color: var(--task-text); font-size: 16px; line-height: 1.35; letter-spacing: -.01em; }
.task-card__open p { display: -webkit-box; max-width: 850px; margin: 7px 0 0; overflow: hidden; color: #a7b1c3; font-size: 12px; line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.task-card__open p.is-empty { color: #707b90; font-style: italic; }
.task-card__meta { flex-wrap: wrap; gap: 12px 22px; margin-top: 19px; }
.task-person,
.task-meta-item { min-width: 150px; gap: 9px; }
.task-avatar {
  display: grid;
  place-items: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(91,124,255,.22);
  border-radius: 9px;
  color: #b9c6ff;
  background: rgba(91,124,255,.1);
  font-size: 10px;
  font-weight: 800;
}
.task-avatar--driver { border-color: rgba(66,215,135,.2); color: #7ae7ad; background: rgba(66,215,135,.09); }
.task-avatar--assignee { border-color: rgba(169,138,255,.18); color: #c6b4f4; background: rgba(169,138,255,.08); }
.task-person > span:last-child,
.task-meta-item > span { display: flex; min-width: 0; flex-direction: column; }
.task-person small,
.task-meta-item small { color: #778398; font-size: 9px; font-weight: 700; letter-spacing: .055em; text-transform: uppercase; }
.task-person strong,
.task-meta-item strong { overflow: hidden; color: #cfd5e1; font-size: 11px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
.task-meta-item >>> .v-icon { color: #758199 !important; }
.task-meta-item--due.is-overdue strong,
.task-meta-item--due.is-overdue >>> .v-icon { color: var(--task-red) !important; }
.task-meta-item--due.is-today strong,
.task-meta-item--due.is-today >>> .v-icon,
.task-meta-item--due.is-soon strong,
.task-meta-item--due.is-soon >>> .v-icon { color: var(--task-amber) !important; }

.task-card__side {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 18px;
  border-left: 1px solid rgba(255,255,255,.055);
  background: rgba(9, 13, 22, 0.12);
}
.task-status {
  display: inline-flex;
  align-self: flex-end;
  align-items: center;
  min-height: 29px;
  gap: 6px;
  padding: 0 9px;
  border: 1px solid rgba(255,255,255,.08);
  border-radius: 999px;
  color: #aeb8ca;
  background: rgba(255,255,255,.035);
  font-size: 10px;
  font-weight: 800;
}
.task-status > span,
.task-status > i { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.task-status >>> .v-icon { color: currentColor !important; }
.task-status--new { color: #8ea5ff; border-color: rgba(91,124,255,.2); background: rgba(91,124,255,.08); }
.task-status--progress { color: #ffbd66; border-color: rgba(255,189,102,.2); background: rgba(255,189,102,.08); }
.task-status--done { color: #62dda0; border-color: rgba(66,215,135,.2); background: rgba(66,215,135,.08); }
.task-status--cancelled { color: #939db0; }
.task-card__duration { display: flex; flex-direction: column; align-items: flex-end; margin-top: 13px; }
.task-card__duration small { color: #778398; font-size: 9px; text-transform: uppercase; }
.task-card__duration strong { color: #7ae7ad; font-size: 12px; }
.task-card__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-top: auto; padding-top: 14px; }
.task-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  gap: 5px;
  padding: 0 8px;
  border: 1px solid rgba(255,255,255,.07);
  border-radius: 9px;
  color: #aeb8ca;
  background: rgba(255,255,255,.025);
  font-size: 10px;
  font-weight: 700;
  transition: .16s ease;
}
.task-action:hover { border-color: rgba(119,146,255,.25); color: #dfe4ed; background: rgba(91,124,255,.08); }
.task-action >>> .v-icon { color: inherit !important; }
.task-action--open { color: #aebeff; }
.task-action--assign { color: #ffcf87; }
.task-action--complete { color: #75e5a8; }
.task-action--delete { color: #8b95a8; }
.task-action--delete:hover { border-color: rgba(255,115,115,.2); color: #ff8b8b; background: rgba(255,115,115,.07); }

.task-skeletons { padding: 10px; }
.task-skeleton { margin-bottom: 8px; border: 1px solid var(--task-border); border-radius: 14px; background: #242a39 !important; }
.tasks-empty { display: flex; align-items: center; flex-direction: column; padding: 70px 20px; text-align: center; }
.tasks-empty > span { display: grid; place-items: center; width: 66px; height: 66px; margin-bottom: 16px; border: 1px solid rgba(91,124,255,.16); border-radius: 20px; color: #8da4ff; background: rgba(91,124,255,.07); }
.tasks-empty > span >>> .v-icon { color: inherit !important; }
.tasks-empty h2 { margin: 0 0 7px; color: var(--task-text); font-size: 18px; }
.tasks-empty p { margin: 0 0 18px; color: var(--task-muted); font-size: 13px; }
.tasks-pagination { justify-content: space-between; gap: 20px; padding: 15px 20px; border-top: 1px solid var(--task-border); color: var(--task-muted); font-size: 11px; }
.tasks-pagination >>> .v-pagination__item,
.tasks-pagination >>> .v-pagination__navigation { color: #cdd3df; background: rgba(255,255,255,.045) !important; box-shadow: none; }
.tasks-pagination >>> .v-pagination__item--active { color: white !important; background: #5b7cff !important; }
.tasks-page-size { max-width: 112px; }

/* Modal content and shared conversation styling */
.task-modal__card { display: flex; flex-direction: column; max-height: 86vh; overflow: hidden; border: 1px solid rgba(255,255,255,.09) !important; border-radius: 20px !important; background: #1d2331 !important; color: #f5f7fc !important; box-shadow: 0 26px 80px rgba(0,0,0,.42) !important; }
.task-modal__header { display: flex; align-items: center; min-height: 92px; gap: 14px; padding: 19px 22px !important; border-bottom: 1px solid rgba(255,255,255,.07); background: linear-gradient(135deg, rgba(91,124,255,.08), transparent 64%); }
.task-modal__icon { display: grid; place-items: center; flex: 0 0 43px; width: 43px; height: 43px; border: 1px solid rgba(91,124,255,.23); border-radius: 13px; color: #9aaeff; background: rgba(91,124,255,.12); }
.task-modal__icon >>> .v-icon { color: inherit !important; }
.task-modal__icon--amber { color: #ffca7e; border-color: rgba(255,189,102,.22); background: rgba(255,189,102,.1); }
.task-modal__icon--green { color: #75e5a8; border-color: rgba(66,215,135,.22); background: rgba(66,215,135,.1); }
.task-modal__icon--violet { color: #c2adf3; border-color: rgba(169,138,255,.22); background: rgba(169,138,255,.1); }
.task-modal__heading { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.task-modal__heading small { color: #8499ec; font-size: 9px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; }
.task-modal__heading strong { color: #f5f7fc; font-size: 18px; line-height: 1.25; }
.task-modal__heading > span { margin-top: 3px; color: #96a2b7; font-size: 11px; font-weight: 400; }
.task-modal__close { color: #8e99ac !important; }
.task-modal__close:hover { color: #f5f7fc !important; background: rgba(255,255,255,.06); }
.task-modal__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 22px !important; color: #dfe4ed !important; scrollbar-color: #414a60 transparent; scrollbar-width: thin; }
.task-modal__header, .task-modal__actions { flex: 0 0 auto; }
/* Auto-grow pola tekstowego z sufitem — dalej rośnie, ale nie rozpycha modala. */
.task-modal__body >>> textarea { max-height: 240px; overflow-y: auto !important; }
.task-modal__body >>> .v-input { color: #dfe4ed; }
.task-modal__body >>> .v-input__slot { background: rgba(10,14,23,.22) !important; }
.task-modal__body >>> fieldset { border-color: rgba(255,255,255,.1) !important; border-radius: 11px !important; }
.task-modal__body >>> .v-input--is-focused fieldset { border-color: rgba(91,124,255,.8) !important; }
.task-modal__body >>> input,
.task-modal__body >>> textarea,
.task-modal__body >>> .v-select__selection { color: #f0f3f9 !important; font-size: 13px; }
.task-modal__body >>> .v-label,
.task-modal__body >>> .v-messages__message { color: #8f9bb0 !important; }
.task-modal__body >>> .v-icon { color: #8490a5; }
.task-modal__section-title { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.task-modal__section-title--spaced { margin-top: 21px; }
.task-modal__section-title > span { display: grid; place-items: center; flex: 0 0 28px; width: 28px; height: 28px; border: 1px solid rgba(91,124,255,.2); border-radius: 9px; color: #91a7ff; background: rgba(91,124,255,.09); font-size: 10px; font-weight: 800; }
.task-modal__section-title > span >>> .v-icon { color: inherit !important; }
.task-modal__section-title > div { display: flex; flex-direction: column; }
.task-modal__section-title strong { color: #e9edf5; font-size: 12px; }
.task-modal__section-title small { color: #7f8ba0; font-size: 10px; }
.task-form-grid { margin-top: -8px; }
.task-upload-zone { padding: 14px; outline: none; border: 1px dashed rgba(116,143,255,.28); border-radius: 13px; background: rgba(91,124,255,.04); transition: border-color .18s ease, background .18s ease; }
.task-upload-zone:focus-within { border-color: rgba(116,143,255,.62); background: rgba(91,124,255,.07); }
.task-upload-zone__head { display: flex; align-items: center; gap: 10px; }
.task-upload-zone__icon { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px; color: #91a7ff; background: rgba(91,124,255,.1); }
.task-upload-zone__icon >>> .v-icon { color: inherit !important; }
.task-upload-zone__head > span:nth-child(2) { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.task-upload-zone__head strong { color: #dfe4ed; font-size: 11px; }
.task-upload-zone__head small { color: #7f8ba0; font-size: 9px; }
.task-upload-list { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.task-upload-item { position: relative; width: 104px; padding: 5px; overflow: visible; border: 1px solid rgba(255,255,255,.08); border-radius: 10px; background: rgba(8,12,20,.35); }
.task-upload-item img,
.task-upload-item__file { display: grid; place-items: center; width: 100%; height: 62px; object-fit: cover; border-radius: 7px; background: rgba(255,255,255,.035); }
.task-upload-item span { display: block; padding: 5px 2px 1px; overflow: hidden; color: #aeb8ca; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.task-upload-item__remove { position: absolute !important; top: -7px; right: -7px; color: #ff8b8b !important; background: #272e3f !important; box-shadow: 0 3px 10px rgba(0,0,0,.25); }
.task-modal__thread-section { margin-top: 22px; }
.task-context-card { display: flex; flex-direction: column; gap: 3px; margin-bottom: 12px; padding: 13px 15px; border: 1px solid rgba(91,124,255,.15); border-radius: 12px; background: rgba(91,124,255,.055); }
.task-context-card--success { border-color: rgba(66,215,135,.17); background: rgba(66,215,135,.055); }
.task-context-card__eyebrow { color: #8199f5; font-size: 8px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.task-context-card strong { color: #edf0f7; font-size: 13px; }
.task-context-card small { display: flex; align-items: center; gap: 5px; color: #8e9aae; font-size: 10px; }
.task-context-card small >>> .v-icon { color: #75e5a8 !important; }
.task-thread { max-height: 320px; padding: 12px; overflow-y: auto; border: 1px solid rgba(255,255,255,.065); border-radius: 13px; background: rgba(8,12,20,.27); scrollbar-color: #414a60 transparent; scrollbar-width: thin; }
.task-thread--compact { max-height: 210px; }
.task-thread--reply { max-height: 270px; margin-bottom: 15px; }
.task-thread--preview { max-height: 350px; margin-top: 14px; }
.task-thread .admin-note,
.task-thread .driver-note,
.task-thread .system-note { border-radius: 10px !important; }
.task-thread img { max-width: min(100%, 320px) !important; height: auto !important; border-radius: 9px !important; }
.task-composer { padding: 14px; border: 1px solid rgba(91,124,255,.1); border-radius: 14px; background: rgba(91,124,255,.035); }
.task-inline-info { display: flex; align-items: flex-start; gap: 9px; margin-top: 2px; padding: 10px 12px; border-radius: 10px; color: #b6c2d5; background: rgba(255,189,102,.07); font-size: 10px; line-height: 1.45; }
.task-inline-info >>> .v-icon { color: #ffbd66 !important; }
.task-inline-info--success { background: rgba(66,215,135,.07); }
.task-inline-info--success >>> .v-icon { color: #62dda0 !important; }
.task-modal__actions { min-height: 66px; gap: 8px; padding: 11px 20px !important; border-top: 1px solid rgba(255,255,255,.07); background: rgba(9,13,22,.16); }
.task-modal__actions--compact { min-height: 52px; padding-top: 4px !important; border-top: 0; background: transparent; }
.task-modal__cancel { min-height: 39px; color: #9da8ba !important; border-radius: 10px !important; text-transform: none !important; letter-spacing: 0 !important; }
.task-modal__submit { min-height: 40px; padding: 0 18px !important; border-radius: 10px !important; color: white !important; text-transform: none !important; letter-spacing: 0 !important; font-weight: 700 !important; box-shadow: 0 8px 20px rgba(79,112,245,.2); }
.task-modal__submit--amber { color: #171b27 !important; }
.task-modal__submit--green { color: #10271c !important; }
.task-preview-summary { padding: 16px; border: 1px solid rgba(255,255,255,.07); border-radius: 14px; background: linear-gradient(135deg, rgba(91,124,255,.06), rgba(255,255,255,.018)); }
.task-preview-summary__title > span { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.task-preview-summary__title h2 { margin: 10px 0 0; color: #f5f7fc; font-size: 18px; line-height: 1.35; }
.task-preview-summary__grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(255,255,255,.06); }
.task-preview-summary__grid > span { position: relative; display: grid; grid-template-columns: 22px minmax(0,1fr); }
.task-preview-summary__grid >>> .v-icon { grid-row: 1 / 3; color: #78859b !important; }
.task-preview-summary__grid small { color: #778398; font-size: 8px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
.task-preview-summary__grid strong { overflow: hidden; color: #cfd6e2; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.task-completion-strip { display: flex; align-items: center; gap: 9px; margin-top: 10px; padding: 10px 13px; border: 1px solid rgba(66,215,135,.14); border-radius: 11px; color: #67dfa2; background: rgba(66,215,135,.055); }
.task-completion-strip >>> .v-icon { color: inherit !important; }
.task-completion-strip span { display: flex; flex-direction: column; }
.task-completion-strip small { color: #8fae9d; font-size: 9px; }
.task-completion-strip strong { font-size: 11px; }
.task-preview-composer { display: flex; align-items: center; gap: 9px; margin: 0 22px; padding: 13px 0 5px; border-top: 1px solid rgba(255,255,255,.07); }
.task-preview-composer__icon { display: grid; place-items: center; flex: 0 0 34px; width: 34px; height: 34px; border-radius: 10px; color: #91a7ff; background: rgba(91,124,255,.09); }
.task-preview-composer__icon >>> .v-icon { color: inherit !important; }
.task-preview-composer >>> .v-input { margin: 0; }
.task-preview-composer >>> .v-input__slot { min-height: 42px !important; background: rgba(10,14,23,.25) !important; }
.task-preview-composer >>> fieldset { border-color: rgba(255,255,255,.09) !important; border-radius: 11px !important; }
.task-preview-composer >>> input { color: #f0f3f9 !important; }
.task-stats-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 9px; }
.task-stats-grid article { display: grid; grid-template-columns: 34px 1fr; padding: 13px; border: 1px solid rgba(255,255,255,.07); border-radius: 13px; background: rgba(255,255,255,.025); }
.task-stats-grid article > span { display: grid; place-items: center; grid-row: 1 / 3; width: 30px; height: 30px; border-radius: 9px; color: #91a7ff; background: rgba(91,124,255,.1); }
.task-stats-grid article > span >>> .v-icon { color: inherit !important; }
.task-stats-grid article small { color: #8390a5; font-size: 8px; font-weight: 800; text-transform: uppercase; }
.task-stats-grid article strong { color: #edf0f7; font-size: 17px; line-height: 1.2; }
.task-stats-grid .is-tone-1 > span { color: #8ea5ff; }
.task-stats-grid .is-tone-2 > span { color: #ffbd66; background: rgba(255,189,102,.09); }
.task-stats-grid .is-tone-3 > span { color: #62dda0; background: rgba(66,215,135,.09); }
.task-stats-grid .is-tone-4 > span { color: #c2adf3; background: rgba(169,138,255,.09); }
.task-stats-team { margin-top: 22px; }
.task-stats-table { overflow: hidden; border: 1px solid rgba(255,255,255,.07); border-radius: 13px; background: rgba(10,14,23,.18) !important; }
.task-stats-table >>> table { background: transparent; }
.task-stats-table >>> th { color: #8e9aae !important; background: rgba(255,255,255,.025) !important; border-color: rgba(255,255,255,.06) !important; }
.task-stats-table >>> td { color: #d8dee9 !important; border-color: rgba(255,255,255,.055) !important; }

@keyframes taskCardEnter {
  from { opacity: 0; transform: translateY(7px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1100px) {
  .tasks-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .task-metric__hint { display: none; }
  .task-card { grid-template-columns: minmax(0,1fr) 230px; }
  .task-star { right: 244px; }
  .task-preview-summary__grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .task-stats-grid { grid-template-columns: repeat(3, minmax(0,1fr)); }
}

@media (max-width: 760px) {
  .tasks-page { margin: -16px; padding: 12px; min-height: calc(100vh - 56px); }
  .tasks-hero { padding: 18px; border-radius: 17px; }
  .tasks-hero__top { align-items: stretch; flex-direction: column; }
  .tasks-hero__actions { display: grid; grid-template-columns: 1fr 1fr; }
  .tasks-metrics { grid-template-columns: repeat(2, minmax(0,1fr)); margin-top: 18px; }
  .task-metric { padding: 10px; }
  .task-metric__copy strong { font-size: 16px; }
  .tasks-workspace { border-radius: 17px; }
  .tasks-toolbar { padding: 14px; }
  .task-tabs { overflow-x: auto; padding-bottom: 2px; scrollbar-width: none; }
  .task-tab { flex: 0 0 auto; }
  .tasks-toolbar__controls { align-items: stretch; flex-wrap: wrap; }
  .task-search { flex: 1 1 100%; }
  .task-sort { flex: 1 1 48%; }
  .task-filter-toggle { flex: 1 1 44%; }
  .task-list { padding: 7px; }
  .task-card { grid-template-columns: 1fr; }
  .task-card__content { padding: 18px 48px 14px 20px; }
  .task-star { top: 12px; right: 12px; }
  .task-card__meta { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 10px; }
  .task-person, .task-meta-item { min-width: 0; }
  .task-card__side { padding: 12px 18px 17px; border-top: 1px solid rgba(255,255,255,.055); border-left: 0; }
  .task-status { align-self: flex-start; }
  .task-card__duration { position: absolute; right: 18px; bottom: 77px; }
  .task-card__actions { grid-template-columns: repeat(2,minmax(0,1fr)); margin-top: 11px; }
  .tasks-pagination { align-items: stretch; flex-direction: column; }
  .tasks-pagination > span { text-align: center; }
  .tasks-page-size { display: none; }
  .task-modal__header { min-height: 82px; padding: 16px !important; }
  .task-modal__heading > span { display: none; }
  .task-modal__body { padding: 16px !important; }
  .task-upload-zone__head { align-items: stretch; flex-direction: column; }
  .task-upload-zone__icon { display: none; }
  .task-preview-summary__grid { grid-template-columns: 1fr 1fr; }
  .task-preview-composer { margin: 0 16px; }
  .task-preview-composer__icon { display: none; }
  .task-stats-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
}

@media (max-width: 440px) {
  .tasks-hero__identity { gap: 11px; }
  .tasks-hero__icon { flex-basis: 40px; width: 40px; height: 40px; border-radius: 12px; }
  .tasks-hero p { font-size: 12px; }
  .tasks-hero__actions { grid-template-columns: 1fr; }
  .tasks-metrics { gap: 7px; }
  .task-metric__icon { display: none; }
  .task-metric { min-height: 58px; }
  .tasks-results-bar { align-items: flex-start; flex-direction: column; gap: 5px; }
  .task-sort, .task-filter-toggle { flex-basis: 100%; }
  .task-card__meta { grid-template-columns: 1fr; }
  .task-action { min-height: 40px; }
  .task-modal__icon { flex-basis: 38px; width: 38px; height: 38px; }
  .task-modal__heading strong { font-size: 15px; }
  .task-modal__section-title small { display: none; }
  .task-preview-summary__grid { grid-template-columns: 1fr; }
  .task-preview-composer { align-items: stretch; flex-direction: column; }
  .task-preview-composer .task-modal__submit { width: 100%; }
  .task-modal__actions { flex-wrap: wrap; }
  .task-modal__actions .task-modal__cancel,
  .task-modal__actions .task-modal__submit { flex: 1 1 42%; }
  .task-stats-grid { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .task-card { animation: none; transition: none; }
  .task-button { transition: none; }
}

/* === Iteracja czytelności: mniej danych, wyraźniejsza kolejność pracy === */
.tasks-hero { padding: 19px 22px; margin-bottom: 12px; }
.tasks-workspace { overflow: visible; }
.tasks-hero__icon { flex-basis: 42px; width: 42px; height: 42px; }
.tasks-hero h1 { font-size: 28px; }
.tasks-hero p { margin-top: 5px; }
.tasks-metrics { gap: 8px; margin-top: 16px; }
.task-metric { padding: 10px 12px; }
.task-metric__icon { flex-basis: 30px; width: 30px; height: 30px; }
.task-metric__copy strong { font-size: 17px; }
.tasks-toolbar { position: sticky; z-index: 4; top: 0; padding: 14px 18px 12px; border-radius: 20px 20px 0 0; background: rgba(30, 36, 50, .97); backdrop-filter: blur(14px); }
.task-tabs { margin-bottom: 11px; }
.tasks-toolbar__controls { max-width: 820px; }
.task-search { flex-basis: 520px; }
.task-quick-filters { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.task-quick-filters > span { margin-right: 2px; color: #718097; font-size: 9px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; }
.task-quick-filters button { display: inline-flex; align-items: center; min-height: 31px; gap: 5px; padding: 0 9px; border: 1px solid rgba(255,255,255,.07); border-radius: 9px; color: #8e9aae; background: rgba(255,255,255,.025); font: inherit; font-size: 10px; font-weight: 750; cursor: pointer; transition: .16s ease; }
.task-quick-filters button >>> .v-icon { color: inherit !important; }
.task-quick-filters button small { min-width: 18px; padding: 1px 5px; border-radius: 999px; color: #aeb8ca; background: rgba(255,255,255,.065); font-size: 9px; }
.task-quick-filters button:hover { color: #dbe1eb; border-color: rgba(119,146,255,.2); }
.task-quick-filters button.is-active { color: #b8c5ff; border-color: rgba(91,124,255,.28); background: rgba(91,124,255,.11); }
.task-quick-filters button.is-active small { color: #e5e9ff; background: rgba(91,124,255,.2); }
.task-quick-filters button.is-urgent.is-active { color: #ff9a9a; border-color: rgba(255,115,115,.25); background: rgba(255,115,115,.085); }
.tasks-results-bar { padding: 10px 18px; }
.tasks-results-bar__filter { color: #78869b; }

.task-groups { padding: 12px; }
.task-group + .task-group { margin-top: 18px; }
.task-group__header { display: flex; align-items: center; gap: 10px; padding: 0 7px 8px; }
.task-group__header > span { display: grid; place-items: center; width: 31px; height: 31px; border-radius: 9px; color: #8fa5fa; background: rgba(91,124,255,.085); }
.task-group__header > span >>> .v-icon { color: inherit !important; }
.task-group__header div { min-width: 0; }
.task-group__header h2 { margin: 0; color: #dfe4ed; font-size: 13px; line-height: 1.25; }
.task-group__header p { margin: 2px 0 0; color: #727f94; font-size: 9px; }
.task-group--danger .task-group__header > span { color: #ff8585; background: rgba(255,115,115,.09); }
.task-group--danger .task-group__header h2 { color: #ffaaaa; }
.task-group--today .task-group__header > span,
.task-group--soon .task-group__header > span { color: #ffca7e; background: rgba(255,189,102,.085); }
.task-group--done .task-group__header > span { color: #6ce1a5; background: rgba(66,215,135,.085); }
.task-group--cancelled .task-group__header > span { color: #8b95a7; background: rgba(139,149,167,.08); }
.task-group .task-list { padding: 0; }

.task-card { grid-template-columns: minmax(0, 1fr) 205px; min-height: 0; cursor: pointer; outline: none; }
.task-card:focus-visible { border-color: rgba(119,146,255,.6); box-shadow: 0 0 0 3px rgba(91,124,255,.15); }
.task-card.is-selected { border-color: rgba(119,146,255,.44); background: linear-gradient(135deg, rgba(48,58,80,.92), rgba(35,42,58,.95)); box-shadow: 0 12px 34px rgba(5,8,18,.22), inset 0 0 0 1px rgba(119,146,255,.08); }
.task-card__content { padding: 18px 22px 15px; }
.task-card__badges { margin-bottom: 8px; }
.task-badge--important { color: #ffca7e; border-color: rgba(255,189,102,.18); background: rgba(255,189,102,.07); }
.task-badge--unread { color: #78e4aa; border-color: rgba(66,215,135,.22); background: rgba(66,215,135,.085); }
.task-badge--unread > i { width: 6px; height: 6px; border-radius: 50%; background: #42d787; box-shadow: 0 0 0 4px rgba(66,215,135,.1); }
.task-card__open { cursor: pointer; }
.task-card__open h2 { max-width: 1000px; font-size: 18px; line-height: 1.32; }
.task-title-star { margin-right: 6px; color: #ffbd66 !important; vertical-align: -2px; }
.task-card__open p { max-width: 1000px; margin-top: 5px; font-size: 12px; -webkit-line-clamp: 2; }
.task-card__focus { display: grid; grid-template-columns: minmax(190px, .8fr) minmax(220px, 1fr); gap: 9px; max-width: 760px; margin-top: 14px; }
.task-focus-item { display: flex; align-items: center; min-width: 0; gap: 9px; padding: 9px 11px; border: 1px solid rgba(255,255,255,.06); border-radius: 11px; background: rgba(8,12,20,.19); }
.task-focus-item > span:last-child { display: flex; min-width: 0; flex-direction: column; }
.task-focus-item small { color: #758197; font-size: 8px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
.task-focus-item strong { overflow: hidden; color: #dce2ec; font-size: 12px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
.task-focus-item em { color: #758197; font-size: 9px; font-style: normal; line-height: 1.25; }
.task-focus-item--owner.is-unassigned { border-color: rgba(255,189,102,.18); background: rgba(255,189,102,.045); }
.task-focus-item--owner.is-unassigned strong { color: #ffca7e; }
.task-focus-item--deadline > span:first-child { display: grid; place-items: center; flex: 0 0 30px; width: 30px; height: 30px; border-radius: 9px; color: #8593aa; background: rgba(255,255,255,.045); }
.task-focus-item--deadline > span:first-child >>> .v-icon { color: inherit !important; }
.task-focus-item--deadline.is-overdue { border-color: rgba(255,115,115,.22); background: rgba(255,115,115,.055); }
.task-focus-item--deadline.is-overdue > span:first-child,
.task-focus-item--deadline.is-overdue strong { color: #ff8f8f; }
.task-focus-item--deadline.is-today,
.task-focus-item--deadline.is-soon { border-color: rgba(255,189,102,.18); background: rgba(255,189,102,.045); }
.task-focus-item--deadline.is-today > span:first-child,
.task-focus-item--deadline.is-today strong,
.task-focus-item--deadline.is-soon > span:first-child,
.task-focus-item--deadline.is-soon strong { color: #ffca7e; }
.task-card__activity { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; margin-top: 11px; color: #748096; font-size: 9px; }
.task-card__activity > span { display: inline-flex; align-items: center; gap: 5px; }
.task-card__activity >>> .v-icon { color: #6f7c92 !important; }
.task-card__activity .is-driver-reply { color: #72dfa7; font-weight: 700; }
.task-card__activity .is-driver-reply >>> .v-icon { color: inherit !important; }
.task-card__record { opacity: .85; }

.task-card__side { padding: 14px; }
.task-card__side-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.task-status { align-self: auto; cursor: default; }
.task-more { display: grid; place-items: center; width: 34px; height: 34px; border: 1px solid transparent; border-radius: 9px; color: #7d899e; background: transparent; cursor: pointer; transition: .16s ease; }
.task-more:hover { color: #dce2ed; border-color: rgba(255,255,255,.08); background: rgba(255,255,255,.045); }
.task-more >>> .v-icon { color: inherit !important; }
.task-card__duration { align-items: flex-start; margin: 11px 0 0; }
.task-primary-action { display: inline-flex; align-items: center; justify-content: center; width: 100%; min-height: 40px; gap: 7px; margin-top: auto; padding: 0 13px; border: 1px solid rgba(119,146,255,.18); border-radius: 10px; color: #b9c6ff; background: rgba(91,124,255,.085); font: inherit; font-size: 11px; font-weight: 800; cursor: pointer; transition: .16s ease; }
.task-primary-action:hover { color: #fff; border-color: rgba(119,146,255,.35); background: rgba(91,124,255,.16); }
.task-primary-action >>> .v-icon { color: inherit !important; }
.task-primary-action--assign { color: #ffca7e; border-color: rgba(255,189,102,.18); background: rgba(255,189,102,.07); }
.task-primary-action--reply { color: #b9c6ff; }

@media (max-width: 920px) {
  .task-card { grid-template-columns: minmax(0,1fr) 185px; }
  .task-card__focus { grid-template-columns: 1fr; }
}

@media (max-width: 760px) {
  .tasks-hero { padding: 15px; }
  .tasks-metrics { margin-top: 13px; }
  .tasks-toolbar { top: 0; padding: 12px; }
  .task-sort { flex: 1 1 100%; }
  .task-quick-filters { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 2px; scrollbar-width: none; }
  .task-quick-filters > span { display: none; }
  .task-quick-filters button { flex: 0 0 auto; }
  .tasks-results-bar__filter { display: none; }
  .task-groups { padding: 9px; }
  .task-card { grid-template-columns: 1fr; }
  .task-card__content { padding: 16px; }
  .task-card__open h2 { font-size: 16px; }
  .task-card__focus { grid-template-columns: 1fr; gap: 7px; }
  .task-card__side { padding: 10px 16px 14px; }
  .task-card__side-top { position: absolute; top: 12px; right: 12px; }
  .task-card__side-top .task-status { margin-right: 34px; }
  .task-more { position: absolute; right: 0; }
  .task-card__badges { padding-right: 132px; }
  .task-card__duration { position: static; }
  .task-primary-action { margin-top: 10px; }
}

@media (max-width: 440px) {
  .tasks-metrics { grid-template-columns: 1fr 1fr; }
  .task-group__header p { display: none; }
  .task-card__badges { padding-right: 0; margin-top: 42px; }
  .task-card__focus { margin-top: 12px; }
  .task-card__activity { align-items: flex-start; flex-direction: column; gap: 5px; }
}
</style>

<style>
.task-modal.v-dialog {
  overflow: visible;
  border-radius: 20px;
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.44) !important;
}
.task-modal .v-card.task-modal__card {
  background: #1d2331 !important;
}
.task-modal .v-card__title.task-modal__header {
  background: linear-gradient(135deg, rgba(91, 124, 255, 0.08), transparent 64%) !important;
}
.task-modal .v-card__text.task-modal__body {
  background: #1d2331 !important;
}
.task-modal .v-card__actions.task-modal__actions {
  background: rgba(9, 13, 22, 0.16) !important;
}
.task-status-menu {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px !important;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.34) !important;
}
.task-status-menu .v-list { padding: 6px; background: #252c3c !important; }
.task-status-menu .v-list-item { min-height: 38px; border-radius: 8px; color: #dfe4ed !important; }
.task-status-menu .v-list-item:hover { background: rgba(91, 124, 255, 0.09) !important; }
.task-status-menu .v-list-item__icon { margin: 11px 10px 11px 0 !important; }
.task-status-menu .v-list-item__title { font-size: 12px; }
.task-actions-menu .v-divider { margin: 5px 7px; border-color: rgba(255,255,255,.07) !important; }
.task-actions-menu .v-subheader { height: 28px; padding: 0 10px; color: #758197 !important; font-size: 9px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.task-actions-menu__danger .v-list-item__title { color: #ff8b8b !important; }
.task-detail-drawer {
  z-index: 1205 !important;
  border-left: 1px solid rgba(255,255,255,.09) !important;
  border-radius: 20px 0 0 20px;
  background: #1d2331 !important;
  box-shadow: -24px 0 70px rgba(0,0,0,.42) !important;
}
.task-detail-drawer .v-navigation-drawer__content { height: 100%; overflow: hidden; background: #1d2331; }
.task-detail-drawer .task-modal__card { display: flex; height: 100%; flex-direction: column; overflow: hidden; border: 0 !important; border-radius: 20px 0 0 20px !important; box-shadow: none !important; }
.task-detail-drawer .task-modal__header { position: relative; z-index: 2; flex: 0 0 auto; }
.task-detail-drawer .task-modal__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; scrollbar-color: #414a60 transparent; scrollbar-width: thin; }
.task-detail-drawer .task-thread--preview { max-height: min(48vh, 520px); }
.task-detail-drawer .task-preview-composer { position: relative; z-index: 2; flex: 0 0 auto; margin: 0; padding: 13px 20px 16px; border-top: 1px solid rgba(255,255,255,.075); background: #19202d; }
.btp-task-feedback {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 20px !important;
  box-shadow: 0 26px 80px rgba(0, 0, 0, 0.45) !important;
}
.btp-task-feedback .swal2-title { color: #f5f7fc !important; font-size: 21px !important; }
.btp-task-feedback .swal2-html-container { color: #9da8ba !important; font-size: 13px !important; line-height: 1.55 !important; }
.btp-task-confirm-btn,
.btp-task-confirm-cancel {
  min-height: 42px;
  margin: 5px;
  padding: 0 18px;
  border: 0;
  border-radius: 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
}
.btp-task-confirm-btn { color: #fff; background: #5b7cff; box-shadow: 0 8px 20px rgba(79, 112, 245, 0.24); }
.btp-task-confirm-btn--danger { background: #e95f6e; box-shadow: 0 8px 20px rgba(233, 95, 110, 0.2); }
.btp-task-confirm-cancel { color: #aab4c5; background: rgba(255, 255, 255, 0.055); }
.btp-task-toast {
  border: 1px solid rgba(255, 255, 255, 0.09) !important;
  border-radius: 13px !important;
  box-shadow: 0 16px 42px rgba(0, 0, 0, 0.34) !important;
}
.btp-task-toast .swal2-title { color: #f1f4f9 !important; font-size: 13px !important; }
.btp-task-toast .swal2-timer-progress-bar { background: #5b7cff !important; }

@media (max-width: 760px) {
  .task-detail-drawer { border-left: 0 !important; border-radius: 0; }
  .task-detail-drawer .task-modal__card { border-radius: 0 !important; }
  .task-modal.v-dialog {
    width: calc(100% - 20px) !important;
    max-height: calc(100vh - 20px) !important;
    margin: 10px !important;
    overflow-y: auto;
    border-radius: 17px;
  }
}
</style>
